import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { TableDefinition, BfxColDef } from "./TableDefinition";

import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { COLUMN_PRIVATIZATION } from "./TableDefinitionFactory.constants";
import { ColumnDefinition } from "./ColumnDefinition";

// adapter imports

import { ColumnFilterType } from "../model/ColumnFilterType";
import {
  OrderBlotterFilterOrderStates,
  FilterOrderTypes,
  ForwardRollEnum,
} from "../model/FilterTypeEnums";

import { BfxConfigIDRefHelper } from "src/shared/helper-services/bfx-config-id-ref-helper";

// real service imports

import { RateCache } from "../rate/impl/RateCache";

// TODO, embed in PricingService?

import { BasePricingService } from "../lookup/pricing/impl/BasePricingService";
import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";

import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { RateSourceLookupService } from "../lookup/model/impl/RateSourceLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { FixingSourceLookupService } from "../lookup/model/impl/FixingSourceLookupService";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "src/shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";
import { SysFeatures } from "../model/core/SysFeatures";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";

import { OrdersTableMode } from "./OrdersTableMode";

import { forwardRef, Inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  COLUMN_TRADE_INSTRUMENT,
  COLUMN_TRADE_DESK_ID,
  COLUMN_TRADE_ORDER_TYPE,
  COLUMN_TRADE_ORDER_PRODUCT,
  COLUMN_TRADE_VALUE_DATE,
  COLUMN_ORDER_PASS_THROUGH_PARAMETERS,
} from "./TradeTableDefinitionFactory.constants";
import {
  COLUMN_INTER_INSTRUMENT,
  COLUMN_INTER_DESK_ID,
  COLUMN_INTER_ORDER_PRODUCT,
} from "./InterventionTableDefinitionFactory.constants";

import { ORDERS_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { ProfileSettingKeys } from "../model/core/ProfileSettingKeys";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { PricingService } from "../lookup/pricing/PricingService";
import { OrderTypeHelper } from "./orders/OrderTypeHelper";
import { OrderStateHelper } from "./orders/OrderStateHelper";
import { PercentAwayHelper } from "./orders/PercentAwayHelper";
import { StatusIconsHelper } from "./orders/StatusIconsHelper";
import { ValueDateHelper } from "./orders/ValueDateHelper";
import { NdfTrackingFwdPtsHelper } from "./orders/NdfTrackingFwdPtsHelper";
import { IndexType } from "./IndexType";
import { FixedIndexRow } from "./FixedIndexRow";

import { BfxSysConfigUtilService } from "shared/auth-services";
import { ValueFormatterParams, ValueGetterParams } from "ag-grid-community";

import { FixingTimeAdapter } from "../adapter/benchmark/FixingTimeAdapter";
import { MultiLineTooltipValueGetter } from "./tooltip/MultiLineTooltipValueGetter";
import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { PrivatizationHelper } from "./orders/PrivatizationHelper";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";

import {
  EMPTY_AMT_RATE_VALUE,
  EMPTY_TEXT_VALUE,
} from "src/shared/cubiq-services/model/BaseTableDefinitionFactory";
import { FixingDateSCHelper } from "./orders/FixingDateSCHelper";
import { BMARK_DEFAULT_NAME } from "./BmarkClientOrdersTableDefinitionFactory.constants";
import { VolumePerMinuteHelper } from "../mediator/impl/VolumePerMinuteHelper";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import * as OrdersTableDefinitionFactoryConstants from "./OrdersTableDefinitionFactory.constants";
import { ProductIntentRenderer } from "./orders/ProductIntentRenderer";
import { ProductRenderer } from "./orders/ProductRenderer";
import { BMOrderAmountHelper } from "./benchmarks/helpers/BMOrderAmountHelper";
import { BmarkRiskData } from "./benchmarks/BmarkRiskData.model";
import { ValueDateRenderer } from "./orders/ValueDateRenderer";
import { StaticDataPrivatizationType } from "./privatization/enum/StaticDataPrivatizationType";
import { ZERO } from "shared/base-components/number-spinner.component";
import { TSLLabelHelper } from "shared/cubiq-services/model/orders/TSLLabelHelper";
import { LimitOrder } from "shared/cubiq-services/model/orders/LimitOrder";

export const CELL_SELECTION_WIDTH: number = 162;

const DEFAULT_CELL_CLASS: string = "ag-cell-default";
const PRIMARY_KEY: string = "primaryKey";
const OVERRIDE_KEY: string = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_ID;
const PRIMARY_SORT_KEY: string = "as_sort_orderId";

@Injectable()
export class OrdersTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected mode: OrdersTableMode = OrdersTableMode.BASE;

  protected myTrailingStopEnabled: boolean;
  protected myAutFillEnabled: boolean;
  protected myClientTraderEnabled: boolean;
  protected myMarkupEnabled: boolean;
  protected myPrivateCommentsEnabled: boolean;
  protected myFwdEnabled: boolean;
  protected myNdfEnabled: boolean;
  protected myBmarkEnabled: boolean;
  protected myPartFillDisabledTypeText: string = "";
  protected manualExecutionCallNameText: string = "";
  private marketBarEnabled: boolean = true;
  private passThroughParamsEnabled: boolean = true;
  public bmarkShortNm: string;

  public dealtPercentAwayHelper: PercentAwayHelper;
  public basePercentAwayHelper: PercentAwayHelper;
  public myOrderTypeHelper: OrderTypeHelper;
  protected myStatusIconHelper: StatusIconsHelper;
  protected myValueDateHelper: ValueDateHelper;
  protected myOrderStateHelper: OrderStateHelper;
  protected myNdfTrackingFwdPointsHelper: NdfTrackingFwdPtsHelper;
  protected privatizationHelper: PrivatizationHelper;
  protected myFixingDateHelper: FixingDateSCHelper;
  protected defaultSortModel: any = [
    { colId: OrdersTableDefinitionFactoryConstants.COLUMN_BASE_PERCENTAGE, sort: "asc" },
  ];
  protected volumePerMinuteHelper: VolumePerMinuteHelper;
  protected enabledVolumePerMinute: boolean = true;
  protected enabledOBO: boolean = false;
  protected enabledPortfolio: boolean = false;
  protected orderAmountHelper: BMOrderAmountHelper;

  protected tslLabelHelper: TSLLabelHelper;

  constructor(
    profileService: ProfilePromiseActionService,
    protected rateCache: RateCache,
    protected rateSourceLookup: RateSourceLookupService,
    protected myClientLookup: ClientLookupService,
    protected myAccountLookup: AccountLookupService,
    protected mySalesGroupLookup: SalesGroupLookupService,
    protected myTraderGroupLookup: TraderGroupLookupService,
    protected ccyLookupService: CcyLookupService,
    protected ccyPairLookupService: CcyPairLookupService,
    protected userLookupService: UserLookupService,
    protected sysConfigService: SystemConfigPromiseService,
    protected translateService: TranslateService,
    protected myBasePricingService: BasePricingService,
    protected myDealtPricingService: DealtPricingService,
    protected fixingSourceLookupService: FixingSourceLookupService,
    protected algoStrategyLookupService: AlgoStrategyLookupService,
    protected currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    protected cmp: ClientModelPrivatization,
    protected amp: AccountModelPrivatization,
    protected ctmp: ClientTraderModelPrivatization,
    public atdlTooltipUiRep: AtdlTooltipUiRep,
    protected sysConfigUtil: BfxSysConfigUtilService,
    @Inject(forwardRef(() => BfxConfigService)) protected configService,
    protected customFieldLookupService: CustomFieldLookupService,
  ) {
    super(profileService);
    this.dealtPercentAwayHelper = new PercentAwayHelper(
      myDealtPricingService,
      profileService,
      ccyPairLookupService,
    );
    this.basePercentAwayHelper = new PercentAwayHelper(
      myBasePricingService,
      profileService,
      ccyPairLookupService,
    );
    this.myOrderTypeHelper = new OrderTypeHelper(
      this.myPartFillDisabledTypeText,
      this.algoStrategyLookupService,
      this.sysConfigUtil,
    );
    this.myOrderStateHelper = new OrderStateHelper(
      OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_STATE,
      this.translateService,
    );
    this.tslLabelHelper = new TSLLabelHelper(myBasePricingService, myDealtPricingService);

    this.myStatusIconHelper = new StatusIconsHelper(
      this.userLookupService,
      this.sysConfigService,
      this.translateService,
      this.myDealtPricingService,
      this.tslLabelHelper,
      this.myTraderGroupLookup,
      this.sysConfigUtil,
    );
    this.orderAmountHelper = new BMOrderAmountHelper(ccyLookupService);
    this.myValueDateHelper = new ValueDateHelper(currencyPairCalendarLookupService);
    this.myNdfTrackingFwdPointsHelper = new NdfTrackingFwdPtsHelper(
      ccyPairLookupService,
      myBasePricingService,
      myDealtPricingService,
    );
    this.privatizationHelper = new PrivatizationHelper(myClientLookup, myAccountLookup);
    this.myFixingDateHelper = new FixingDateSCHelper();
    this.volumePerMinuteHelper = new VolumePerMinuteHelper(
      this.myTraderGroupLookup,
      this.currencyPairCalendarLookupService,
    );

    this.profileService
      .getProfileConfig(ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences)
      .then((profileValue) => {
        this.mapUserSettings(profileValue);
      });

    this.profileService
      .getProfileConfigUpdates(ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences)
      .subscribe((profileValue) => {
        this.mapUserSettings(profileValue);
      });

    configService.getUIConfig().then((config) => {
      this.enabledOBO = config && config.general ? config.general.oboEnabled : false;
    });

    this.getBmarkOrderDisplayName();
  }

  // this override for system configs applicable for blotter definition
  protected getTableCongiPromises(): Array<Promise<any>> {
    return [
      this.sysConfigService.getSystemConfig(SysFeatures.trailingStopsEnabled), // 0
      this.sysConfigService.getSystemConfig(SysFeatures.clientTraderUsersEnabled), // 1
      this.sysConfigService.getSystemConfig(SysFeatures.autoFillEnabled), // 2
      this.sysConfigService.getSystemConfig(SysFeatures.partFillDisabledTypeText), // 3
      this.sysConfigService.getSystemConfig(SysFeatures.markupEnabled), // 4
      // ROLES
      this.sysConfigService.getSystemConfig(SysFeatures.featuresEnablePrivateComments), // 5
      this.sysConfigService.getSystemConfig(SysFeatures.fwdEnabled), // 6
      this.sysConfigService.getSystemConfig(SysFeatures.ndfEnabled), // 7
      this.sysConfigService.getSystemConfig(SysFeatures.benchmarksEnabled), // 8
      // promises related to services
      this.atdlTooltipUiRep.isReady(), // 9
      //
      this.sysConfigService.getSystemConfig(SysFeatures.passThroughColumEnabled), // 10
    ];
  }

  protected determineDefaultConfig(defaultConfig: any) {
    super.determineDefaultConfig(defaultConfig);
    if (defaultConfig) {
      if (defaultConfig.dealtMode != undefined) {
        this.mode = defaultConfig.dealtMode ? OrdersTableMode.DEALT : OrdersTableMode.BASE;
      }
      if (defaultConfig.ndfMode != undefined && defaultConfig.ndfMode) {
        this.mode =
          this.mode === OrdersTableMode.BASE ? OrdersTableMode.NDF_BASE : OrdersTableMode.NDF_DEALT;
      }
    }
  }

  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    this.myTrailingStopEnabled = this.valueOn(values[0]);
    this.myClientTraderEnabled = this.valueOn(values[1]);
    this.myAutFillEnabled = this.valueOn(values[2]);
    if (values[3] !== undefined || values[3] !== null) {
      // this may be configure to ''
      this.myPartFillDisabledTypeText = values[3];
    }
    this.myMarkupEnabled = this.valueOn(values[4]);
    this.myPrivateCommentsEnabled = this.valueOn(values[5]);
    this.myFwdEnabled = this.valueOn(values[6]);
    this.myNdfEnabled = this.valueOn(values[7]);
    this.myBmarkEnabled = this.valueOn(values[8]);
    this.passThroughParamsEnabled = this.valueOn(values[10]);

    this.myOrderTypeHelper = new OrderTypeHelper(
      this.myPartFillDisabledTypeText,
      this.algoStrategyLookupService,
      this.sysConfigUtil,
    );
  }

  protected initColdDef(): any {
    return { suppressMenu: true, cellClass: [DEFAULT_CELL_CLASS] };
  }

  public createDef(): TableDefinition {
    super.createDef();

    let td = new TableDefinition(
      PRIMARY_KEY,
      PRIMARY_SORT_KEY,
      // new RowDefinition([new StatusRowAdapter()])
    );

    td.setOverwriteKey(OVERRIDE_KEY);

    let selectionColumn: BfxColDef = this.initColdDef();
    selectionColumn.field = "selection";
    selectionColumn.sortingOrder = [];
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.checkboxSelection = true;
    selectionColumn.headerName = "(0)";
    selectionColumn.headerCheckboxSelection = true;
    selectionColumn.headerCheckboxSelectionFilteredOnly = true;
    selectionColumn.suppressSorting = true;
    selectionColumn.suppressMovable = true;
    // selectionColumn.suppressResize = false;
    selectionColumn.suppressMenu = true;
    selectionColumn.columnFilterType = ColumnFilterType.NONE;
    selectionColumn.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      // Set up blotter attributes - invertedBaseMode is constant for blotter
      // StatusIconHelper has a reference to the confingured tslLabelHelper
      this.tslLabelHelper.invertedBaseMode = params.context.isInverted;
      this.tslLabelHelper.ordersTableMode = params.context.mode;

      return this.myStatusIconHelper.getIcons(params.data);
    }.bind(this);
    selectionColumn.cellRenderer = function (this: null, params) {
      return params.value != null ? params.value : "";
    };
    // enforced width on fixed column
    selectionColumn.cellClass = ["status-icon-cell"];
    selectionColumn.width = CELL_SELECTION_WIDTH;
    selectionColumn.minWidth = this.threeWs;
    selectionColumn.maxWidth = CELL_SELECTION_WIDTH;
    selectionColumn.position = this.position.next();
    selectionColumn.omitFromPicker = true;
    selectionColumn.disableInPicker = true;
    selectionColumn.indexType = IndexType.None;
    selectionColumn.tooltipValueGetter = function (this: null) {
      return "";
    };
    td.addColumn(selectionColumn);

    td.addColumn(this.createOrderStateColumn("Order State"));

    td.addColumn(this.createClientNameColumn("Client Name"));

    td.addColumn(this.createClientIdColumn("Client ID"));

    td.addColumn(this.createAccountNameColumn("Account Name"));

    td.addColumn(this.createAccountIdColumn("Account ID"));

    td.addColumn(this.createInstrumentColumn("Instrument"));

    td.addColumn(this.createIntentColumn("Dir"));

    td.addColumn(this.createDealtCCYColumn("Ccy"));

    td.addColumn(this.createOrderAmtColumn("Order Amt"));

    td.addColumn(this.createUSDAmtColumn("Order Amt USD"));

    td.addColumn(this.createRemainingAmtColumn("Open Amt"));

    td.addColumn(this.createRemainingUSDAmtColumn("Open Amt USD"));

    // THIS MUST BE THE FIRST EXECUTED AMOUNT
    td.addColumn(this.createFillAmtColumn("Fill Amt"));

    td.addColumn(this.createFillPercColumn("Fill %"));

    td.addColumn(this.createFillUSDAmtColumn("Fill Amt USD"));

    td.addColumn(this.createAvgClientAllinColumn("Avg Px"));

    td.addColumn(this.createOrderTypeColumn("Order Type"));

    td.addColumn(this.createProductColumn("Product"));

    // dealt spot tracking
    let spotDealtTrackCol: BfxColDef = this.initColdDef();
    // using a headerValueGetter requires ColumnFilterType.NONE
    spotDealtTrackCol.headerValueGetter = function (this: null, params) {
      let name = "Spot Tracking";
      switch (params.context.mode) {
        case OrdersTableMode.NDF_DEALT:
        case OrdersTableMode.NDF_BASE:
          name = "NDF Tracking";
          break;
        case OrdersTableMode.DEALT:
        case OrdersTableMode.BASE:
        default:
          name = "Spot Tracking";
          break;
      }
      params.colDef.headerName = name;
      params.colDef.headerTooltip = name;
      return name;
    };
    spotDealtTrackCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_DEALT_TRACKING;
    spotDealtTrackCol.indexType = IndexType.StrikethroughRate;
    spotDealtTrackCol.cellClass = ["blotter_center_align_numeric blotter_priority_col"];

    spotDealtTrackCol.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let ndfMode: boolean =
        params.context.mode == OrdersTableMode.NDF_BASE ||
        params.context.mode == OrdersTableMode.NDF_DEALT;
      let strikethrough: boolean = ndfMode
        ? this.myBasePricingService.isStrikethroughNdfTracking(params.data)
        : this.myBasePricingService.isStrikethroughSpotTracking(params.data);

      // Spot Tracking Render will replace NaN with EMPTY_AMT_RATE_VALUE
      let value = NaN;

      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
          value = this.myDealtPricingService.getBlotterSpotTracking(params.data);
          break;
        case OrdersTableMode.NDF_DEALT:
          value = this.myDealtPricingService.getBlotterNdfTracking(params.data);
          break;
        case OrdersTableMode.NDF_BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            value = this.myBasePricingService.getInvertNdfTracking(params.data);
          } else {
            value = this.myBasePricingService.getBlotterNdfTracking(params.data);
          }
          break;
        case OrdersTableMode.BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            value = this.myBasePricingService.getInvertSpotTracking(params.data);
          } else {
            value = this.myBasePricingService.getBlotterSpotTracking(params.data);
          }
          break;
      }

      return PricingService.createStrikethroughValue(value, strikethrough);
    }.bind(this);
    spotDealtTrackCol.cellRenderer = "spotTrackingRenderer";
    spotDealtTrackCol.minWidth = this.sixWs;
    spotDealtTrackCol.width = this.sevenWs;
    spotDealtTrackCol.isPrimary = true;
    spotDealtTrackCol.columnFilterType = ColumnFilterType.NONE;
    spotDealtTrackCol.columnFilterData = null;

    // Renderer will show the tooltip
    spotDealtTrackCol.showTooltip = false;

    if (!this.restoreFromConfig(spotDealtTrackCol)) {
      spotDealtTrackCol.position = this.position.next();
    }

    td.addColumn(spotDealtTrackCol);

    if (this.myFwdEnabled || this.myNdfEnabled) {
      let fwdPtsCol: BfxColDef = this.initColdDef();

      (fwdPtsCol.headerName = "Fwd Pts"),
        (fwdPtsCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_DEALT_FWD_PTS);
      fwdPtsCol.indexType = IndexType.StrikethroughRate;
      fwdPtsCol.minWidth = this.oneWs;
      fwdPtsCol.width = this.sevenWs;
      fwdPtsCol.cellClass = ["blotter_center_align_numeric"];
      fwdPtsCol.sortingOrder = [];
      fwdPtsCol.columnFilterType = ColumnFilterType.NONE;
      fwdPtsCol.columnFilterData = null;
      fwdPtsCol.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
        if (params.data?.isLadderAggregate) {
          return null;
        }

        let ret = "-";
        let strikethrough: boolean = false;

        // Algos don't have forward points
        if (params.data != null && params.data.orderType === CubiqTS.OrderType.ALGO) {
          return ret;
        }

        if (params.data.isLadderPair !== true) {
          let ndfMode: boolean =
            params.context.mode == OrdersTableMode.NDF_BASE ||
            params.context.mode == OrdersTableMode.NDF_DEALT;
          strikethrough = ndfMode
            ? this.myBasePricingService.isStrikethroughFwdPtsNdfTracking(params.data)
            : this.myBasePricingService.isStrikethroughFwdPts(params.data);

          if (params.context.isInverted && params.context.isInverted(params.data)) {
            ret = this.myNdfTrackingFwdPointsHelper.getInvertValue(params);
          } else {
            ret = this.myNdfTrackingFwdPointsHelper.getValue(params);
          }
        }

        return PricingService.createStrikethroughValue(ret, strikethrough);
      }.bind(this);

      // Renderer will show the tooltip
      fwdPtsCol.showTooltip = false;

      fwdPtsCol.cellRenderer = "fwdPtsRenderer";

      if (!this.restoreFromConfig(fwdPtsCol)) {
        fwdPtsCol.position = this.position.next();
      }

      td.addColumn(fwdPtsCol);
    }

    if (this.myFwdEnabled || this.myNdfEnabled) {
      let fwdDealtTracking: BfxColDef = this.initColdDef();

      fwdDealtTracking.headerName = "Fwd Tracking";
      fwdDealtTracking.field =
        OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_FWD_DEALT_TRACKING;
      fwdDealtTracking.indexType = IndexType.NaturalString;
      fwdDealtTracking.minWidth = this.oneWs;
      fwdDealtTracking.width = this.sevenWs;
      fwdDealtTracking.isPrimary = true;
      fwdDealtTracking.cellClass = ["blotter_center_align_numeric"];
      fwdDealtTracking.columnFilterType = ColumnFilterType.NONE;
      fwdDealtTracking.columnFilterData = null;
      fwdDealtTracking.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
        switch (params.context.mode) {
          case OrdersTableMode.DEALT:
          case OrdersTableMode.NDF_DEALT:
            return this.myDealtPricingService.getBlotterFwdTracking(params.data);
          case OrdersTableMode.NDF_BASE:
          case OrdersTableMode.BASE:
            if (params.context.isInverted && params.context.isInverted(params.data)) {
              return this.myBasePricingService.getInvertFwdTracking(params.data);
            }
            return this.myBasePricingService.getBlotterFwdTracking(params.data);
        }
      }.bind(this);
      if (!this.restoreFromConfig(fwdDealtTracking)) {
        fwdDealtTracking.position = this.position.next();
      }

      td.addColumn(fwdDealtTracking);
    }

    // dealt percent away
    let dealtPercentAwayColumn: BfxColDef = this.initColdDef();

    dealtPercentAwayColumn.headerName = "% Away";
    dealtPercentAwayColumn.field = OrdersTableDefinitionFactoryConstants.COLUMN_DEALT_PERCENTAGE;
    dealtPercentAwayColumn.sortKey = "percSort";
    dealtPercentAwayColumn.indexType = IndexType.PercAway;
    dealtPercentAwayColumn.disableInPicker = true;
    dealtPercentAwayColumn.cellClass = ["blotter_center_align_numeric blotter_primary_col"];
    dealtPercentAwayColumn.minWidth = this.oneWs;
    dealtPercentAwayColumn.width = this.sixWs;
    dealtPercentAwayColumn.columnFilterType = ColumnFilterType.PERCENTAGE;
    dealtPercentAwayColumn.columnFilterData = null;
    // Renderer will show the tooltip
    dealtPercentAwayColumn.showTooltip = false;
    dealtPercentAwayColumn.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      if (params.data.isMarketBar) {
        return 0;
      }

      let value: number = 0;
      let strikethrough: boolean = this.myBasePricingService.isStrikethroughPercentAway(
        params.data,
      );

      switch (params.context.mode) {
        case OrdersTableMode.NDF_DEALT:
        case OrdersTableMode.DEALT:
          value = this.dealtPercentAwayHelper.updatePercentAway(params.data);
          break;
        case OrdersTableMode.NDF_BASE:
        case OrdersTableMode.BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            value = this.basePercentAwayHelper.updateInvertPercentAway(params.data);
          } else {
            value = this.basePercentAwayHelper.updatePercentAway(params.data);
          }
          break;
      }

      return PricingService.createStrikethroughValue(value, strikethrough);
    }.bind(this);
    dealtPercentAwayColumn.valueFormatter = function (this: null, params) {
      const num: number = PricingService.clearStrikeThroughValue(params.value) as number;

      if (num == null || isNaN(num)) {
        return EMPTY_AMT_RATE_VALUE;
      }

      return num.toFixed(2);
    };
    dealtPercentAwayColumn.getFilterValue = (params) => {
      const num: number = params.perc;

      if (isNaN(num)) {
        return "0"; // zero percent away so will be found or filtered if between 0 and 0.1
      }

      // we need this as string for filtering with Equals/Not Equals operators
      return num + "";
    };
    dealtPercentAwayColumn.cellRenderer = "percentAwayRenderer";
    dealtPercentAwayColumn.sortingOrder = this.marketBarEnabled
      ? ["asc", null]
      : ["asc", "desc", null];

    if (!this.restoreFromConfig(dealtPercentAwayColumn)) {
      dealtPercentAwayColumn.position = this.position.next();
    }

    td.addColumn(dealtPercentAwayColumn);

    // dealt spot market mid
    let dealtSpotMarketMid: BfxColDef = this.initColdDef();
    dealtSpotMarketMid.headerName = "Market Mid";
    dealtSpotMarketMid.field = OrdersTableDefinitionFactoryConstants.COLUMN_SPOT_MARKET_MID;
    dealtSpotMarketMid.cellClass = [];
    dealtSpotMarketMid.minWidth = this.oneWs;
    dealtSpotMarketMid.width = this.sevenWs;
    dealtSpotMarketMid.columnFilterType = ColumnFilterType.NONE;
    dealtSpotMarketMid.columnFilterData = null;
    dealtSpotMarketMid.cellClass = ["blotter_center_align_numeric"];

    // Renderer will show the tooltip
    dealtSpotMarketMid.showTooltip = false;

    dealtSpotMarketMid.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let strikethrough: boolean = this.myBasePricingService.isStrikethroughMarketMid(params.data);
      let value: string = null;

      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          value = this.myDealtPricingService.getBlotterMarketMidRate(params.data);
          break;
        case OrdersTableMode.NDF_BASE:
        case OrdersTableMode.BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            value = this.myBasePricingService.getInvertMarketMidRate(params.data);
          } else {
            value = this.myBasePricingService.getBlotterMarketMidRate(params.data);
          }
          break;
      }

      return PricingService.createStrikethroughValue(value, strikethrough);
    }.bind(this);
    dealtSpotMarketMid.cellRenderer = "marketMidRenderer";
    // not sorted
    dealtSpotMarketMid.sortingOrder = [];
    if (!this.restoreFromConfig(dealtSpotMarketMid)) {
      dealtSpotMarketMid.position = this.position.next();
    }

    td.addColumn(dealtSpotMarketMid);

    // rate source
    let rateSrc: BfxColDef = this.initColdDef();

    rateSrc.headerName = "Rate Src";
    rateSrc.field = OrdersTableDefinitionFactoryConstants.COLUMN_RATE_SOURCE_CODE;
    rateSrc.minWidth = this.oneWs;
    rateSrc.width = this.fiveWs;
    rateSrc.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(rateSrc)) {
      rateSrc.hide = true;
      rateSrc.position = this.position.next();
    }

    td.addColumn(rateSrc);

    //  monitoring side dealt
    let ts: BfxColDef = this.initColdDef();
    ts.headerName = "Side";
    ts.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_MONITORING_SIDE;
    ts.minWidth = this.oneWs;
    ts.width = this.fiveWs;
    ts.cellClass = ["blotter_left_align_col"];
    ts.columnFilterType = ColumnFilterType.NONE;
    ts.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let pricingService: PricingService;
      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          pricingService = this.myDealtPricingService;
          break;
        case OrdersTableMode.NDF_BASE:
        case OrdersTableMode.BASE:
          pricingService = this.myBasePricingService;
          break;
      }

      let trackingSide: CubiqTS.RateSideType = pricingService.getBlotterTrackingSide(params.data);
      if (trackingSide != undefined && trackingSide != null) {
        if (params.context.isInverted && params.context.isInverted(params.data)) {
          trackingSide =
            trackingSide == CubiqTS.RateSideType.BID
              ? CubiqTS.RateSideType.OFFER
              : CubiqTS.RateSideType.BID;
        }
        return this.translateService.instant("RateSideType." + CubiqTS.RateSideType[trackingSide]);
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    if (!this.restoreFromConfig(ts)) {
      ts.position = this.position.next();
    }

    td.addColumn(ts);

    td.addColumn(this.createValueDtColumn("Value Date"));

    td.addColumn(this.createTraderGroupColumn("Trader Group"));

    // hidden outside of find-orders
    let modifyDts: BfxColDef = this.initColdDef();
    modifyDts.headerName = "Last Updated";
    modifyDts.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_MODIFY_DATE;
    modifyDts.minWidth = this.oneWs;
    modifyDts.width = this.nineWs;
    modifyDts.columnFilterType = ColumnFilterType.NONE;
    modifyDts.valueFormatter = this.formatMomment.bind(this);
    if (!this.restoreFromConfig(modifyDts)) {
      modifyDts.position = this.position.next();
      modifyDts.hide = true;
      modifyDts.omitFromPicker = true;
    } else {
      modifyDts.hide = true;
      modifyDts.omitFromPicker = true;
    }

    td.addColumn(modifyDts);

    td.addColumn(this.createGoodUntilColumn("Good Until"));

    td.addColumn(this.createClientCommentsColumn("Client Comments"));

    if (this.myPrivateCommentsEnabled) {
      td.addColumn(this.createPrivateCommentsColumn("Private Comments"));
    }

    // entry user name
    let enUser: BfxColDef = this.initColdDef();
    enUser.headerName = "Entry User";
    enUser.field = OrdersTableDefinitionFactoryConstants.COLUMN_ENTRY_USER_ID;
    enUser.minWidth = this.oneWs;
    enUser.width = this.sixWs;
    enUser.columnFilterType = ColumnFilterType.USER;
    enUser.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let user = this.userLookupService.getItem(
        params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ENTRY_USER_ID],
      );
      if (user) {
        return user.userCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    enUser.valueFormatter = function (this: OrdersTableDefinitionFactory, params) {
      let user = this.userLookupService.getItem(
        params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ENTRY_USER_ID],
      );
      if (user) {
        return this.showUserName ? user.userName : user.userCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);

    if (!this.restoreFromConfig(enUser)) {
      enUser.position = this.position.next();
    }

    td.addColumn(enUser);

    // entry date
    let entryDate: BfxColDef = this.initColdDef();
    entryDate.headerName = "Entry Date";
    entryDate.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_ENTRY_DATE;
    entryDate.minWidth = this.oneWs;
    entryDate.width = this.sixWs;
    entryDate.columnFilterType = ColumnFilterType.NONE;
    entryDate.valueFormatter = this.formatMomment.bind(this);

    if (!this.restoreFromConfig(entryDate)) {
      entryDate.hide = true;
      entryDate.position = this.position.next();
    }

    td.addColumn(entryDate);

    // dealt order rate
    let requestRate: BfxColDef = this.initColdDef();
    requestRate.headerName = "Request Rate";
    requestRate.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_REQUEST_RATE;
    requestRate.cellClass = [];
    requestRate.minWidth = this.oneWs;
    requestRate.cellClass = ["blotter_center_align_numeric"];
    requestRate.valueFormatter = (params: ValueFormatterParams) => {
      if (params.data?.isLadderAggregate) {
        return "";
      }

      if (params.value != null) {
        let pricingService: PricingService = this.myDealtPricingService;
        let order: CubiqTS.OrderDataImpl = params.data;
        let value = pricingService.getOrderRequestRt(order);
        if (CubiqTS.BigNumber.isBigNumber(value)) {
          return value.toString();
        }
        return value;
      }

      return EMPTY_AMT_RATE_VALUE;
    };
    requestRate.columnFilterType = ColumnFilterType.NONE;
    requestRate.width = this.sevenWs;
    if (!this.restoreFromConfig(requestRate)) {
      requestRate.position = this.position.next();
    }

    td.addColumn(requestRate);

    // order id
    let orId: BfxColDef = this.initColdDef();
    orId.headerName = "Order ID";
    orId.cellClass = ["numeric"];
    orId.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_ID;
    orId.minWidth = this.twoWs;
    orId.width = BfxConfigIDRefHelper.getOrderRefWs();
    orId.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(orId)) {
      orId.position = this.position.next();
    }
    orId.valueFormatter = BfxConfigIDRefHelper.orderRefValueFormatter;
    orId.getQuickFilterText = BfxConfigIDRefHelper.orderRefValueFormatter;

    td.addColumn(orId);

    td.addColumn(this.createStrategyIdColumn("Strategy ID"));

    // clOrderId
    let clientOrderId: BfxColDef = this.initColdDef();
    clientOrderId.headerName = "ClOrderId";
    clientOrderId.field = OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_ORDER_ID;
    clientOrderId.minWidth = this.oneWs;
    clientOrderId.width = this.sixWs;
    clientOrderId.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clientOrderId)) {
      clientOrderId.hide = true;
      clientOrderId.position = this.position.next();
    }

    td.addColumn(clientOrderId);

    // salesGroup
    let salesGroup: BfxColDef = this.initColdDef();
    salesGroup.headerName = "Sales Group";
    salesGroup.field = OrdersTableDefinitionFactoryConstants.COLUMN_SALES_GROUP_ID;
    salesGroup.minWidth = this.oneWs;
    salesGroup.width = this.sevenWs;
    salesGroup.columnFilterType = ColumnFilterType.SALESGROUP;
    salesGroup.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let sg = this.mySalesGroupLookup.getSalesGroup(
        params.data[OrdersTableDefinitionFactoryConstants.COLUMN_SALES_GROUP_ID],
      );
      if (sg) {
        return sg.groupCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    if (!this.restoreFromConfig(salesGroup)) {
      salesGroup.hide = true;
      salesGroup.position = this.position.next();
    }

    td.addColumn(salesGroup);

    // client branch
    let clientBranch: BfxColDef = this.initColdDef();
    clientBranch.headerName = "Branch";
    clientBranch.field = OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_BRANCH;
    clientBranch.minWidth = this.oneWs;
    clientBranch.width = this.fourWs;
    clientBranch.columnFilterType = ColumnFilterType.BRANCH;
    if (!this.restoreFromConfig(clientBranch)) {
      clientBranch.hide = true;
      clientBranch.position = this.position.next();
    }

    td.addColumn(clientBranch);

    if (this.myClientTraderEnabled) {
      // Client Trader
      let clTrader: BfxColDef = this.initColdDef();
      clTrader.headerName = "Client Trader";
      clTrader.field = OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_TRADER;
      clTrader.alias = [OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_TRADER];
      clTrader.minWidth = this.oneWs;
      clTrader.width = this.sevenWs;
      clTrader.columnFilterType = ColumnFilterType.USER;
      clTrader.columnFilterData = CubiqTS.UserType.CLIENT_TRADER;
      clTrader.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
        return this.privatizationHelper.getPrivatizedValue(
          params,
          OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_TRADER,
        );
      }.bind(this);
      if (!this.restoreFromConfig(clTrader)) {
        clTrader.hide = true;
        clTrader.position = this.position.next();
      }

      td.addColumn(clTrader);
    }

    // channel
    let channel: BfxColDef = this.initColdDef();
    channel.headerName = "Channel";
    channel.field = "channelCd";
    channel.minWidth = this.oneWs;
    channel.width = this.fiveWs;
    channel.columnFilterType = ColumnFilterType.CHANNEL;
    if (!this.restoreFromConfig(channel)) {
      channel.hide = true;
      channel.position = this.position.next();
    }
    channel.valueGetter = (params) => {
      let channelCd: string;
      channelCd = params.data.channelCd;
      return channelCd != null && typeof channelCd === "string" && channelCd.length > 0
        ? channelCd
        : null;
    };
    channel.valueFormatter = (params) => {
      let channelCd: string = params.data.channelCd;
      let displayName: string;
      if (channelCd != null && typeof channelCd === "string" && channelCd.length > 0) {
        displayName = this.myClientLookup.getTradingChannelProfileDisplayName(channelCd);
      }

      return displayName != null && typeof displayName === "string" && displayName.length > 0
        ? displayName
        : EMPTY_TEXT_VALUE;
    };

    td.addColumn(channel);

    if (this.myAutFillEnabled) {
      // auto eligible
      let ae: BfxColDef = this.initColdDef();
      ae.headerName = "Auto Eligible";
      ae.field = "isAutoEligible";
      ae.minWidth = this.oneWs;
      ae.width = this.sevenWs;
      ae.valueFormatter = this.formatBoolean;
      ae.cellClass = [];
      ae.columnFilterType = ColumnFilterType.BOOLEAN;
      if (!this.restoreFromConfig(ae)) {
        ae.hide = true;
        ae.position = this.position.next();
      }

      td.addColumn(ae);

      // execution channel
      let execChannel: BfxColDef = this.initColdDef();
      execChannel.headerName = "Exec Channel";
      execChannel.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTION_CHANNEL;
      execChannel.minWidth = this.oneWs;
      execChannel.width = this.sevenWs;
      execChannel.columnFilterType = ColumnFilterType.ENUM;
      execChannel.columnFilterData = CubiqTS.ExecutionType;
      execChannel.valueGetter = (param) => {
        if (
          CubiqTS.ExecutionType[
            param.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTION_CHANNEL]
          ] != null
        )
          return this.translateService.instant(
            "column.filter.executionType." +
              CubiqTS.ExecutionType[
                param.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTION_CHANNEL]
              ],
          );

        return EMPTY_TEXT_VALUE;
      };
      if (!this.restoreFromConfig(execChannel)) {
        execChannel.hide = true;
        execChannel.position = this.position.next();
      }

      td.addColumn(execChannel);
    }

    // value getters are usually inline - and this doesn't need a full helper
    const suspensionDetailsValueGetterFn = function suspensionValueGetterFn(param) {
      const orderData: CubiqTS.OrderData = param.data;
      const suspensionDetails: CubiqTS.SuspensionDetails = orderData.suspensionDetails;
      const field = param.colDef.field;
      if (suspensionDetails != null) {
        return suspensionDetails[field];
      }
      return null;
    }.bind(this);

    // suspend from
    let susFrom: BfxColDef = this.initColdDef();
    susFrom.headerName = "Suspend From";
    susFrom.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_SUSPENSION_FROM_DATE;
    susFrom.minWidth = this.oneWs;
    susFrom.width = this.eightWs;
    susFrom.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(susFrom)) {
      susFrom.hide = true;
      susFrom.position = this.position.next();
    }
    susFrom.valueGetter = suspensionDetailsValueGetterFn;
    susFrom.valueFormatter = this.formatMomment.bind(this);

    td.addColumn(susFrom);

    // suspend to
    let susTo: BfxColDef = this.initColdDef();
    susTo.headerName = "Suspend To";
    susTo.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_SUSPENSION_TO_DATE;
    susTo.minWidth = this.oneWs;
    susTo.width = this.eightWs;
    susTo.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(susTo)) {
      susTo.hide = true;
      susTo.position = this.position.next();
    }
    susTo.valueGetter = suspensionDetailsValueGetterFn;
    susTo.valueFormatter = this.formatMomment.bind(this);

    td.addColumn(susTo);

    // sibling
    let sibling: BfxColDef = this.initColdDef();
    sibling.headerName = "";
    sibling.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_SIBLING;
    sibling.hide = true;
    sibling.omitFromPicker = true;
    sibling.columnFilterType = ColumnFilterType.NONE;
    sibling.position = this.position.next();
    sibling.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data;
      return (
        order != null && order.siblingRelationship != null && order.siblingRelationship.orderId
      );
    };

    td.addColumn(sibling);

    const strategyParams: BfxColDef = this.createStartegyParamsColumn();
    td.addColumn(strategyParams);

    // Pass-Through Params
    if (this.passThroughParamsEnabled) {
      let passThroughParameters: BfxColDef = this.initColdDef();
      passThroughParameters.headerName = "Pass-Through Parameters";
      passThroughParameters.colSelectorName = "Pass-Through ...";
      passThroughParameters.field = COLUMN_ORDER_PASS_THROUGH_PARAMETERS;
      //passThroughParameters.sortingOrder = [];
      passThroughParameters.minWidth = this.oneWs;
      passThroughParameters.width = this.twelveWs;
      passThroughParameters.columnFilterType = ColumnFilterType.NONE;
      passThroughParameters.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
        let order: CubiqTS.OrderDataImpl = params.data;
        if (
          order.passthroughParameters == null ||
          !Array.isArray(order.passthroughParameters) ||
          order.passthroughParameters.length < 1
        ) {
          return EMPTY_TEXT_VALUE;
        }

        let passThroughParametersValues: CubiqTS.StrategyParameterImpl[] =
          this.atdlTooltipUiRep.inlineUiRep("Pass-Through Parameters", order.passthroughParameters);
        let value = "";

        if (passThroughParametersValues != null && passThroughParametersValues.length > 0) {
          passThroughParametersValues = passThroughParametersValues.sort(
            (a: CubiqTS.StrategyParameterImpl, b: CubiqTS.StrategyParameterImpl) =>
              a.parameterName == null ? -1 : a.parameterName.localeCompare(b.parameterName),
          );
        }

        if (passThroughParametersValues != null && Array.isArray(passThroughParametersValues)) {
          for (const [i, passThroughParamItem] of passThroughParametersValues.entries()) {
            value += `${passThroughParamItem.parameterName}: ${passThroughParamItem.parameterValue}`;

            if (i < passThroughParametersValues.length - 1) value += `; `;
          }
        }
        return value;
      }.bind(this);
      passThroughParameters.tooltipValueGetter = MultiLineTooltipValueGetter;
      if (!this.restoreFromConfig(passThroughParameters)) {
        passThroughParameters.hide = true;
        passThroughParameters.position = this.position.next();
      }

      td.addColumn(passThroughParameters);
    }

    // target group
    let tgGroup: BfxColDef = this.initColdDef();
    tgGroup.headerName = "Target Group";
    tgGroup.field = OrdersTableDefinitionFactoryConstants.COLUMN_TARGET_DESK_ID;
    tgGroup.minWidth = this.oneWs;
    tgGroup.width = this.sevenWs;
    tgGroup.columnFilterType = ColumnFilterType.NONE;
    tgGroup.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let tg = this.myTraderGroupLookup.getTraderGroup(
        params.data[OrdersTableDefinitionFactoryConstants.COLUMN_TARGET_DESK_ID],
      );
      if (tg) {
        return tg.groupCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);
    if (!this.restoreFromConfig(tgGroup)) {
      tgGroup.hide = true;
      tgGroup.position = this.position.next();
    }

    td.addColumn(tgGroup);

    if (this.myTrailingStopEnabled) {
      // trailing trigger
      let trTrigger: BfxColDef = this.initColdDef();
      trTrigger.headerName = "Trailing Trigger";
      trTrigger.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_TRAILING_TRIGGER;

      trTrigger.minWidth = this.oneWs;
      trTrigger.width = this.sevenWs;
      trTrigger.cellClass = [];
      trTrigger.columnFilterType = ColumnFilterType.NONE;
      if (!this.restoreFromConfig(trTrigger)) {
        trTrigger.hide = true;
        trTrigger.position = this.position.next();
      }
      trTrigger.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
        // Set up blotter attributes - invertedBaseMode is constant for blotter
        if (params.data?.isLadderAggregate) {
          return "";
        }

        this.tslLabelHelper.invertedBaseMode = params.context.isInverted;
        this.tslLabelHelper.ordersTableMode = params.context.mode;
        const order: CubiqTS.OrderDataImpl = params.data;
        return this.tslLabelHelper.getTrailingTriggerLabel(order);
      }.bind(this);

      td.addColumn(trTrigger);

      // trailing pips
      let trPips: BfxColDef = this.initColdDef();
      trPips.headerName = "Trailing Pips";
      trPips.field = "trailingPips";

      trPips.minWidth = this.oneWs;
      trPips.width = this.sixWs;
      trPips.columnFilterType = ColumnFilterType.NONE;
      trPips.cellClass = ["blotter_amt_align"];
      trPips.valueGetter = (params) => {
        if (params.data?.isLadderAggregate) {
          return "";
        }

        // Set up blotter attributes - invertedBaseMode is constant for blotter
        this.tslLabelHelper.invertedBaseMode = params.context.isInverted;
        this.tslLabelHelper.ordersTableMode = params.context.mode;
        const order: CubiqTS.OrderDataImpl = params.data;
        return this.tslLabelHelper.getTrailingPipsLabel(order);
      };

      if (!this.restoreFromConfig(trPips)) {
        trPips.hide = true;
        trPips.position = this.position.next();
      }

      td.addColumn(trPips);
    }

    if (this.myMarkupEnabled) {
      // dealt markup
      let markupCol: BfxColDef = this.initColdDef();
      markupCol.headerName = "Markup";
      markupCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_MARKUP;
      markupCol.indexType = IndexType.String;
      markupCol.cellClass = ["blotter_amt_align"];
      markupCol.minWidth = this.oneWs;
      markupCol.width = this.sevenWs;
      markupCol.hide = true;
      markupCol.columnFilterType = ColumnFilterType.NONE;
      markupCol.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
        let pricingService: PricingService;
        let order: CubiqTS.OrderDataImpl = params.data;
        let isDealt: boolean = false;
        switch (params.context.mode) {
          case OrdersTableMode.DEALT:
          case OrdersTableMode.NDF_DEALT:
            pricingService = this.myDealtPricingService;
            isDealt = true;
            break;
          case OrdersTableMode.NDF_BASE:
          case OrdersTableMode.BASE:
            pricingService = this.myBasePricingService;
            isDealt = false;
            break;
        }
        if (pricingService) {
          if (isDealt) {
            return pricingService.getDealtMarkup(order);
          } else {
            if (params.context.isInverted && params.context.isInverted(params.data)) {
              return pricingService.getInvertMarkup(order);
            }
            return pricingService.getBaseMarkup(order);
          }
        }
        return "0";
      }.bind(this);
      markupCol.omitFromPicker = false;
      if (!this.restoreFromConfig(markupCol)) {
        markupCol.hide = true;
        markupCol.position = this.position.next();
      }

      td.addColumn(markupCol);
    }

    let eo: BfxColDef = this.initColdDef();
    eo.headerName = "External Order";
    eo.field = "isExternal";
    eo.minWidth = this.oneWs;
    eo.width = this.threeWs;
    eo.cellClass = [];
    eo.hide = true;
    eo.valueFormatter = this.formatBoolean;
    eo.omitFromPicker = true;
    eo.columnFilterType = ColumnFilterType.NONE;
    eo.position = this.position.next();

    td.addColumn(eo);

    let eto: BfxColDef = this.initColdDef();
    eto.headerName = "Target External Order";
    eto.field = "isTargetExternal";
    eto.minWidth = this.oneWs;
    eto.width = this.threeWs;
    eto.cellClass = [];
    eto.hide = true;
    eto.omitFromPicker = true;
    eto.columnFilterType = ColumnFilterType.NONE;
    eto.position = this.position.next();

    td.addColumn(eto);

    // hidden column for quick fill dialog
    let clientSTPCol: BfxColDef = this.initColdDef();
    clientSTPCol.headerName = "Client STP";
    clientSTPCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_STP;
    clientSTPCol.minWidth = this.oneWs;
    clientSTPCol.width = this.sixWs;
    clientSTPCol.hide = true;
    clientSTPCol.omitFromPicker = true;
    clientSTPCol.columnFilterType = ColumnFilterType.NONE;
    clientSTPCol.position = this.position.next();

    td.addColumn(clientSTPCol);

    // hidden column
    let privatizationCol: BfxColDef = this.initColdDef();
    // Client,Account adapters handle this
    privatizationCol.headerName = "Privatization";
    privatizationCol.field = COLUMN_PRIVATIZATION;

    privatizationCol.minWidth = this.oneWs;
    privatizationCol.width = this.sixWs;
    privatizationCol.hide = true;
    privatizationCol.omitFromPicker = true;
    privatizationCol.columnFilterType = ColumnFilterType.NONE;
    privatizationCol.position = this.position.next();

    td.addColumn(privatizationCol);

    // NDF fixing code
    if (this.myNdfEnabled) {
      let fixingCd: BfxColDef = this.initColdDef();
      fixingCd.headerName = "Fixing Code";
      fixingCd.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_FIXING_SOURCE_CODE;
      fixingCd.minWidth = this.oneWs;
      fixingCd.width = this.sixWs;
      fixingCd.omitFromPicker = false;
      fixingCd.columnFilterType = ColumnFilterType.FIXCODE;
      fixingCd.valueGetter = (params: ValueGetterParams) => {
        if (params.data?.isLadderAggregate) {
          return "";
        }

        let order: CubiqTS.OrderDataImpl = params.data;
        let val = order[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_FIXING_SOURCE_CODE];

        if (val != null && typeof val === "string") {
          return val;
        }

        if (CubiqTS.Product.NDF === order["product"]) {
          let desc: string = this.translateService.instant("fixingSource.custom");
          return desc;
        }

        return EMPTY_TEXT_VALUE;
      };
      if (!this.restoreFromConfig(fixingCd)) {
        fixingCd.hide = true;
        fixingCd.position = this.position.next();
      }

      td.addColumn(fixingCd);
    }

    // fixing date
    if (this.myNdfEnabled) {
      let fixingDate: BfxColDef = this.initColdDef();
      fixingDate.headerName = "Fixing Date";
      fixingDate.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_FIXING_DATE;
      fixingDate.minWidth = this.oneWs;
      fixingDate.width = this.sixWs;
      fixingDate.omitFromPicker = false;
      fixingDate.indexType = IndexType.FixingDate;
      fixingDate.columnFilterType = ColumnFilterType.NONE;
      fixingDate.valueGetter = ((params: ValueGetterParams) => {
        if (params.data && params.data.fixingDate) {
          return this.getFixingTimeValue(params.data, false, false);
        }
        return EMPTY_TEXT_VALUE;
      }).bind(this);
      if (!this.restoreFromConfig(fixingDate)) {
        fixingDate.hide = true;
        fixingDate.position = this.position.next();
      }

      td.addColumn(fixingDate);
    }

    // // hidden column
    // let algoCol: BfxColDef = this.initColdDef();
    // // Whether this order is an Algo
    // algoCol.headerName = "isAlgo";
    // algoCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_IS_ALGO;
    // algoCol.minWidth = this.oneWs;
    // algoCol.width = this.threeWs;
    // algoCol.hide = true;
    // algoCol.omitFromPicker = true;
    // algoCol.columnFilterType = ColumnFilterType.NONE;
    // algoCol.columnFilterData = null;
    // algoCol.position = this.position.next();
    // td.addColumn(algoCol);

    td.addColumn(this.createSTPHoldColumn("Hold STP"));

    // Position
    let positionCol = this.initColdDef();
    positionCol.headerName = "Position";
    positionCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_LADDER_POSITION;
    positionCol.visible = false;
    positionCol.omitFromPicker = true;
    positionCol.exportable = true;
    positionCol.minWidth = this.oneWs;
    positionCol.width = this.fiveWs;
    positionCol.columnFilterType = ColumnFilterType.NONE;
    positionCol.columnFilterData = null;
    positionCol.indexType = IndexType.NaturalString;
    positionCol.minWidth = this.oneWs;
    positionCol.width = this.eightWs;
    positionCol.isPriority = true;
    positionCol.cellClass = ["blotter_amt_align"];
    positionCol.valueGetter = (params) => {
      if (params.data.isLadderAggregate && params.data.aggregateExpanded) {
        return "";
      }

      return params.data[positionCol.field];
    };
    if (!this.restoreFromConfig(positionCol)) {
      positionCol.hide = true;
      positionCol.position = this.position.next();
    }

    td.addColumn(positionCol);

    // after adding all columns, add any fixed filterable rows
    let bar = this.marketBarEnabled ? this.getMarketBar(td) : null;
    if (bar) {
      dealtPercentAwayColumn.fixedIndexRows = [new FixedIndexRow(bar)];
    }

    if (this.enabledOBO) {
      // Reset here for new columns
      this.position.set(50);

      // OBO Client
      let oboClient: BfxColDef = this.initColdDef();
      oboClient.headerName = "OBO Client";
      oboClient.field = "oboClient";
      oboClient.minWidth = this.oneWs;
      oboClient.width = this.sevenWs;
      oboClient.columnFilterType = ColumnFilterType.NONE;
      oboClient.valueFormatter = (params: ValueFormatterParams) => {
        if (params.data?.isLadderAggregate) {
          return "";
        }

        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboClient)) {
        oboClient.hide = true;
        oboClient.position = this.position.next();
      }

      td.addColumn(oboClient);

      // OBO Account
      let oboAccount: BfxColDef = this.initColdDef();
      oboAccount.headerName = "OBO Account";
      oboAccount.field = "oboAccount";
      oboAccount.minWidth = this.oneWs;
      oboAccount.width = this.sevenWs;
      oboAccount.columnFilterType = ColumnFilterType.NONE;
      oboAccount.valueFormatter = (params: ValueFormatterParams) => {
        if (params.data?.isLadderAggregate) {
          return "";
        }

        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboAccount)) {
        oboAccount.hide = true;
        oboAccount.position = this.position.next();
      }

      td.addColumn(oboAccount);

      let oboEntryUser: BfxColDef = this.initColdDef();
      oboEntryUser.headerName = "OBO Entry User";
      oboEntryUser.field = "oboEntryUser";
      oboEntryUser.minWidth = this.oneWs;
      oboEntryUser.width = this.sevenWs;
      oboEntryUser.columnFilterType = ColumnFilterType.NONE;
      oboEntryUser.valueFormatter = (params: ValueFormatterParams) => {
        if (params.data?.isLadderAggregate) {
          return "";
        }

        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(oboEntryUser)) {
        oboEntryUser.hide = true;
        oboEntryUser.position = this.position.next();
      }

      td.addColumn(oboEntryUser);
    }

    // Reset here for new columns
    this.position.set(80);

    // Custom fields
    let custFieldDefns: Array<CubiqTS.CustomFieldImpl> =
      this.customFieldLookupService.getAllCustomFields();

    custFieldDefns.forEach((item) => {
      if (item.isEnabledFg) {
        const position = 70 + this.customFieldLookupService.getCustomFieldOrdinal(item);
        td.addColumn(this.createCustomFieldColumn(item.displayName, item.fieldId, position));
      }
    });

    this.position.set(95);

    td.addColumn(this.createSTPAggColumn("STP Aggregation", "STP Aggreg..."));

    td.addColumn(this.createFwdRollColumn("Forward Roll"));

    if (this.enabledPortfolio) {
      // Netting ID (Portfolio ID) Col
      const nettingId: BfxColDef = this.initColdDef();
      nettingId.headerName = "Spot Portfolio";
      nettingId.field = "nettingId";
      nettingId.minWidth = this.oneWs;
      nettingId.width = this.sevenWs;
      nettingId.columnFilterType = ColumnFilterType.NONE;
      nettingId.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(nettingId)) {
        nettingId.hide = true;
        nettingId.position = this.position.next();
      }

      td.addColumn(nettingId);

      // Netting ID (Portfolio ID) Col
      const fwdNettingId: BfxColDef = this.initColdDef();
      fwdNettingId.headerName = "Forward Portfolio";
      fwdNettingId.field = "fwdNettingId";
      fwdNettingId.minWidth = this.oneWs;
      fwdNettingId.width = this.sevenWs;
      fwdNettingId.columnFilterType = ColumnFilterType.NONE;
      fwdNettingId.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null ? "-" : params.value;
      };

      if (!this.restoreFromConfig(fwdNettingId)) {
        fwdNettingId.hide = true;
        fwdNettingId.position = this.position.next();
      }

      td.addColumn(fwdNettingId);
    }

    return td;
  }
  createFwdRollColumn(title: string): BfxColDef {
    const forwardRoll: BfxColDef = this.initColdDef();
    forwardRoll.headerName = title;
    forwardRoll.field = OrdersTableDefinitionFactoryConstants.COLUMN_FWD_ROLL;
    forwardRoll.minWidth = this.oneWs;
    forwardRoll.width = this.sevenWs;

    forwardRoll.columnFilterType = ColumnFilterType.ENUM;
    forwardRoll.columnFilterData = ForwardRollEnum;

    // Convert value to string to make it easier to sort
    forwardRoll.valueGetter = (params: ValueGetterParams) => {
      if (params.data?.isLadderAggregate) {
        return "";
      }

      let retVal = "-";
      if (params.data instanceof CubiqTS.OrderDataImpl) {
        const order = params.data;
        let relevant: boolean =
          order.orderType === CubiqTS.OrderType.BENCHMARK ||
          order.orderType === CubiqTS.OrderType.BMALGO ||
          order.orderType === CubiqTS.OrderType.ALGO;
        if (
          relevant &&
          order.product !== CubiqTS.Product.SPT &&
          order.product !== CubiqTS.Product.NDF &&
          order.autoForwardPricingFg != null
        ) {
          retVal = true == order.autoForwardPricingFg ? "Auto" : "Manual";
        }
      }
      return retVal;
    };
    forwardRoll.indexType = IndexType.String;

    // We need to convert the tri-state boolean into an enum for filtering
    forwardRoll.getFilterValue = (order: CubiqTS.OrderDataImpl) => {
      let retVal: String = ForwardRollEnum[ForwardRollEnum.NONE];
      if (order instanceof CubiqTS.OrderDataImpl) {
        let relevant: boolean =
          order.orderType === CubiqTS.OrderType.BENCHMARK ||
          order.orderType === CubiqTS.OrderType.BMALGO ||
          order.orderType === CubiqTS.OrderType.ALGO;

        if (
          relevant &&
          order.product !== CubiqTS.Product.SPT &&
          order.product !== CubiqTS.Product.NDF &&
          order.autoForwardPricingFg != null
        ) {
          retVal =
            order.autoForwardPricingFg === true
              ? ForwardRollEnum[ForwardRollEnum.AUTO]
              : ForwardRollEnum[ForwardRollEnum.MANUAL];
        }
      }
      return retVal;
    };

    if (!this.restoreFromConfig(forwardRoll)) {
      forwardRoll.hide = true;
      forwardRoll.position = this.position.next();
    }
    return forwardRoll;
  }
  createSTPAggColumn(title: string, colSelName: string): BfxColDef {
    let stpAgg: BfxColDef = this.initColdDef();
    stpAgg.headerName = title;
    stpAgg.colSelectorName = colSelName;
    stpAgg.field = OrdersTableDefinitionFactoryConstants.COLUMN_STP_AGG;
    stpAgg.minWidth = this.oneWs;
    stpAgg.width = this.sixWs;
    stpAgg.valueGetter = (params: ValueGetterParams) => {
      return (
        // false will be default value
        params.data instanceof CubiqTS.OrderDataImpl &&
        OrderTypeHelper.canSTPAggregate(params.data) &&
        params.data.stpAggregationEnabled
      );
    };
    stpAgg.columnFilterType = ColumnFilterType.BOOLEAN;

    stpAgg.valueFormatter = (params: ValueFormatterParams) => {
      if (params.data?.isLadderAggregate) {
        return "";
      }

      let val: string = "-";

      if (OrderTypeHelper.canSTPAggregate(params.data)) {
        val = params.value === true ? "Yes" : "No";
      }

      return val;
    };

    if (!this.restoreFromConfig(stpAgg)) {
      stpAgg.hide = true;
      stpAgg.position = this.position.next();
    }
    return stpAgg;
  }

  createCustomFieldColumn(displayName: string, fieldId: string, position: number): BfxColDef {
    let customField: BfxColDef = this.initColdDef();
    customField.headerName = displayName;
    customField.colSelectorName = this.limitLength(displayName, 10);
    customField.field = fieldId;
    customField.minWidth = this.oneWs;
    customField.width = this.sevenWs;
    customField.columnFilterType = ColumnFilterType.NONE;
    customField.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let order: CubiqTS.OrderDataImpl = params.data;
      if (order != null) {
        let customValues: Array<CubiqTS.CustomValueImpl> = order.customValues;
        if (customValues != null) {
          for (let i: number = 0; i < customValues.length; i++) {
            if (customValues[i].fieldId === customField.field) {
              return customValues[i].value;
            }
          }
        }
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);

    if (!this.restoreFromConfig(customField)) {
      customField.hide = true;
      customField.position = position;
      if (customField.position == null) {
        customField.position = this.position.next();
      }
    }
    return customField;
  }
  createSTPHoldColumn(title: string): BfxColDef {
    let stph: BfxColDef = this.initColdDef();
    stph.headerName = title;
    stph.field = OrdersTableDefinitionFactoryConstants.COLUMN_STP_HOLD;
    stph.minWidth = this.oneWs;
    stph.width = this.sixWs;
    stph.omitFromPicker = false;
    stph.valueFormatter = this.formatBoolean;
    stph.columnFilterType = ColumnFilterType.BOOLEAN;
    stph.cellClass = [];
    if (!this.restoreFromConfig(stph)) {
      stph.hide = true;
      stph.position = this.position.next();
    }
    return stph;
  }
  createStrategyIdColumn(title: string): BfxColDef {
    let strategyId: BfxColDef = this.initColdDef();
    strategyId.headerName = "Strategy ID";
    strategyId.field = OrdersTableDefinitionFactoryConstants.COLUMN_STRATEGY_ID;
    strategyId.cellClass = ["numeric"];
    strategyId.minWidth = this.oneWs;
    strategyId.width = BfxConfigIDRefHelper.getStrategyRefWs();
    strategyId.columnFilterType = ColumnFilterType.NONE;
    strategyId.valueFormatter = BfxConfigIDRefHelper.strategyRefValueFormatter;
    strategyId.getQuickFilterText = BfxConfigIDRefHelper.strategyRefValueFormatter;
    if (!this.restoreFromConfig(strategyId)) {
      strategyId.hide = true;
      strategyId.position = this.position.next();
    }
    return strategyId;
  }
  createPrivateCommentsColumn(title: string): BfxColDef {
    let clComm2: BfxColDef = this.initColdDef();
    clComm2.headerName = title;
    clComm2.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_PRIVATE_COMMENT;
    clComm2.minWidth = this.oneWs;
    clComm2.width = this.nineWs;
    clComm2.showTooltip = true;
    clComm2.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clComm2)) {
      clComm2.position = this.position.next();
    }
    return clComm2;
  }

  createClientCommentsColumn(title: string): BfxColDef {
    let clComm1: BfxColDef = this.initColdDef();

    clComm1.headerName = title;
    clComm1.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_CLIENT_COMMENTS;
    clComm1.minWidth = this.oneWs;
    clComm1.width = this.nineWs;
    clComm1.showTooltip = true;
    clComm1.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(clComm1)) {
      clComm1.position = this.position.next();
    }
    return clComm1;
  }
  createGoodUntilColumn(title: string): BfxColDef {
    let exDt: BfxColDef = this.initColdDef();
    exDt.headerName = title;
    exDt.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXPIRY_DATE;
    exDt.valueFormatter = this.goodUntilFormatter.bind(this);
    exDt.minWidth = this.oneWs;
    exDt.width = this.fiveWs;
    exDt.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(exDt)) {
      exDt.position = this.position.next();
    }
    return exDt;
  }
  createTraderGroupColumn(title: string): BfxColDef {
    let dkId: BfxColDef = this.initColdDef();
    dkId.headerName = title;
    dkId.field = OrdersTableDefinitionFactoryConstants.COLUMN_DESK_ID;
    dkId.alias = [COLUMN_TRADE_DESK_ID, COLUMN_INTER_DESK_ID];
    dkId.minWidth = this.oneWs;
    dkId.width = this.sevenWs;
    dkId.columnFilterType = ColumnFilterType.TRADERGROUP;
    dkId.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let tg = this.myTraderGroupLookup.getTraderGroup(
        params.data[OrdersTableDefinitionFactoryConstants.COLUMN_DESK_ID],
      );
      if (tg) {
        return tg.groupCode;
      }
      return EMPTY_TEXT_VALUE;
    }.bind(this);

    dkId.getFilterValue = (data: any) => {
      let values = [];

      let tg = this.myTraderGroupLookup.getTraderGroup(
        data[OrdersTableDefinitionFactoryConstants.COLUMN_DESK_ID],
      );

      if (tg && tg.groupId != null) {
        values.push(tg.groupId.toString());
      }

      if (data[OrdersTableDefinitionFactoryConstants.COLUMN_TARGET_DESK_ID] != null) {
        tg = this.myTraderGroupLookup.getTraderGroup(
          data[OrdersTableDefinitionFactoryConstants.COLUMN_TARGET_DESK_ID],
        );

        if (tg && tg.groupId != null) {
          values.push(tg.groupId.toString());
        }
      }

      return values;
    };

    if (!this.restoreFromConfig(dkId)) {
      dkId.position = this.position.next();
    }
    return dkId;
  }
  createOrderTypeColumn(title: string): BfxColDef {
    let orderTypeCd: BfxColDef = this.initColdDef();
    orderTypeCd.headerName = title;
    orderTypeCd.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_TYPE;
    orderTypeCd.cellRenderer = OrdersTableDefinitionFactoryConstants.RENDERER_ORDER_TYPE_FUNC;
    orderTypeCd.indexType = IndexType.String;
    orderTypeCd.alias = [COLUMN_TRADE_ORDER_TYPE];
    orderTypeCd.minWidth = this.oneWs;
    orderTypeCd.width = this.sixWs;
    orderTypeCd.cellClass = ["blotter_center_align ordertype_col"];
    orderTypeCd.columnFilterType = ColumnFilterType.ORDERTYPE;
    orderTypeCd.columnFilterData = FilterOrderTypes;
    orderTypeCd.valueGetter = (params) => {
      if (params.context.isInverted && params.context.isInverted(params.data)) {
        return this.myOrderTypeHelper.getInvertType(params.data);
      }
      return this.myOrderTypeHelper.getType(params.data);
    };
    orderTypeCd.getFilterValue = (data) => {
      return this.myOrderTypeHelper.getFilterOrderType(data);
    };
    if (!this.restoreFromConfig(orderTypeCd)) {
      orderTypeCd.position = this.position.next();
    }

    return orderTypeCd;
  }
  createAvgClientAllinColumn(title: string): BfxColDef {
    let vwapCol: BfxColDef = this.initColdDef();
    vwapCol.headerName = title;
    vwapCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_VWAP_ALL_IN;
    vwapCol.indexType = IndexType.NaturalString;
    vwapCol.cellClass = [];
    vwapCol.minWidth = this.oneWs;
    vwapCol.cellClass = ["blotter_center_align_numeric"];
    vwapCol.width = this.fourWs;
    vwapCol.columnFilterType = ColumnFilterType.NONE;
    vwapCol.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      if (params.data?.isLadderAggregate) {
        return "";
      }

      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          return this.myDealtPricingService.getVwapDealt(params.data);
        case OrdersTableMode.NDF_BASE:
        case OrdersTableMode.BASE:
          return this.myBasePricingService.getVwapBase(
            params.data,
            params.context.isInverted && params.context.isInverted(params.data),
          );
      }
    }.bind(this);

    if (!this.restoreFromConfig(vwapCol)) {
      vwapCol.hide = true;
      vwapCol.position = this.position.next();
    }
    return vwapCol;
  }
  createFillUSDAmtColumn(title: string): BfxColDef {
    const executionUsdAmtCd: BfxColDef = this.initColdDef();
    executionUsdAmtCd.headerName = title;
    executionUsdAmtCd.field =
      OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTED_AMOUNT_USD;
    executionUsdAmtCd.indexType = IndexType.NaturalString;
    executionUsdAmtCd.minWidth = this.oneWs;
    executionUsdAmtCd.width = this.eightWs;
    executionUsdAmtCd.isPriority = true;
    executionUsdAmtCd.cellClass = ["blotter_amt_align"];
    executionUsdAmtCd.valueFormatter = (params: ValueFormatterParams) => {
      return this.formatUsdAmount(params);
    };
    executionUsdAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    executionUsdAmtCd.columnFilterData = null;
    executionUsdAmtCd.cellRenderer = "fillRenderer";
    executionUsdAmtCd.cellRendererParams = {
      getOrderAmount: (params) =>
        params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_AMOUNT],
      getExecAmount: (params) => {
        let order: CubiqTS.OrderDataImpl = params.data;
        if (order.leavesQty != null && order.orderQty != null) {
          return order.orderQty.minus(order.leavesQty);
        }
        return params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTED_AMOUNT];
      },
    };

    if (!this.restoreFromConfig(executionUsdAmtCd)) {
      executionUsdAmtCd.hide = true;
      executionUsdAmtCd.position = this.position.next();
    }
    return executionUsdAmtCd;
  }
  createFillPercColumn(title: string): BfxColDef {
    const fillPerc: BfxColDef = this.initColdDef();
    fillPerc.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTED_PERC;
    fillPerc.headerName = title;
    fillPerc.indexType = IndexType.NaturalString;
    fillPerc.columnFilterType = ColumnFilterType.PERCENTAGE;
    fillPerc.minWidth = this.oneWs;
    fillPerc.width = this.fiveWs;
    fillPerc.isPriority = true;
    fillPerc.cellClass = ["blotter_amt_align"];
    fillPerc.columnFilterData = null;
    fillPerc.valueGetter = (params) => {
      if (params.data.calculateExecutedPercentage && params.data[fillPerc.field] == null) {
        params.data.calculateExecutedPercentage(params.context);
      }

      return params.data[fillPerc.field];
    };
    fillPerc.valueFormatter = this.formatPercent;
    fillPerc.cellRenderer = "fillPercRenderer";
    if (!this.restoreFromConfig(fillPerc)) {
      fillPerc.hide = true;
      fillPerc.position = this.position.next();
    }

    return fillPerc;
  }
  createFillAmtColumn(title: string): BfxColDef {
    const executionAmtCd: BfxColDef = this.initColdDef();

    executionAmtCd.headerName = title;
    executionAmtCd.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTED_AMOUNT;
    executionAmtCd.indexType = IndexType.NaturalString;
    executionAmtCd.minWidth = this.oneWs;
    executionAmtCd.width = this.eightWs;
    executionAmtCd.isPriority = true;
    executionAmtCd.cellClass = ["blotter_amt_align"];
    executionAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    executionAmtCd.columnFilterData = null;
    executionAmtCd.valueGetter = function (this: null, params) {
      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          return params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTED_AMOUNT];
        case OrdersTableMode.BASE:
        case OrdersTableMode.NDF_BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            if (params.data.baseCode != params.data.fixedCode) {
              return params.data.executedQty;
            }
            return params.data.executedSettleQty;
          }
          if (params.data.executedQty && params.data.baseCode == params.data.fixedCode) {
            return params.data.executedQty;
          } else if (params.data.executedSettleQty) {
            return params.data.executedSettleQty;
          }
      }
      return null;
    };
    executionAmtCd.valueFormatter = (params) => {
      const order: CubiqTS.OrderDataImpl = params.data;
      let amountString;

      if (CubiqTS.RequestOrderType.BMARK === order.requestedOrderType) {
        let riskData: BmarkRiskData = params.data.risk;
        if (null != riskData && riskData.isSwap) {
          const isFilled =
            order.orderState === CubiqTS.OrderState.FILLED ||
            order.orderState === CubiqTS.OrderState.PENDING_FWDS ||
            order.orderState === CubiqTS.OrderState.EXPIRY_PENDING_FWDS ||
            order.orderState === CubiqTS.OrderState.CANCEL_PENDING_FWDS;
          amountString = this.orderAmountHelper.formatAmount(
            riskData.fixedCcy,
            isFilled ? riskData.riskAmount : ZERO,
            riskData.isSwap,
            isFilled ? riskData.nearAmount : ZERO,
            isFilled ? riskData.farAmount : ZERO,
          );
        }
      }
      if (!amountString) {
        amountString = this.formatCcyAmount(order.fixedCode, params.value);
      }
      return amountString;
    };
    executionAmtCd.tooltipValueGetter = (params) => {
      const order: CubiqTS.OrderDataImpl = params.data;

      if (CubiqTS.RequestOrderType.BMARK === order.requestedOrderType) {
        let riskData: BmarkRiskData = params.data.risk;
        if (null != riskData && riskData.isSwap) {
          const isFilled =
            order.orderState === CubiqTS.OrderState.FILLED ||
            order.orderState === CubiqTS.OrderState.PENDING_FWDS ||
            order.orderState === CubiqTS.OrderState.EXPIRY_PENDING_FWDS ||
            order.orderState === CubiqTS.OrderState.CANCEL_PENDING_FWDS;
          return this.orderAmountHelper.formatAmountTooltip(
            isFilled ? riskData.riskAmount : ZERO,
            riskData.isSwap,
            isFilled ? riskData.nearAmount : ZERO,
            isFilled ? riskData.farAmount : ZERO,
            riskData?.fixedCcy,
          );
        }
      }
      return params.valueFormatted;
    };

    if (!this.restoreFromConfig(executionAmtCd)) {
      executionAmtCd.hide = true;
      executionAmtCd.position = this.position.next();
    }
    return executionAmtCd;
  }
  createRemainingUSDAmtColumn(title: string): BfxColDef {
    // dealt usd equiv open(remaining) amount
    const remainingUsdAmtCd: BfxColDef = this.initColdDef();
    remainingUsdAmtCd.headerName = title;
    remainingUsdAmtCd.field = OrdersTableDefinitionFactoryConstants.COLUMN_REMAINING_AMOUNT_USD;
    remainingUsdAmtCd.indexType = IndexType.NaturalString;
    remainingUsdAmtCd.minWidth = this.oneWs;
    remainingUsdAmtCd.width = this.eightWs;
    remainingUsdAmtCd.cellClass = ["blotter_amt_align"];
    remainingUsdAmtCd.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (order == null || order.orderType == CubiqTS.OrderType.LIMIT_CALL) {
        return null;
      }

      return order[OrdersTableDefinitionFactoryConstants.COLUMN_REMAINING_AMOUNT_USD];
    };
    remainingUsdAmtCd.valueFormatter = (params: ValueFormatterParams) => {
      return this.formatUsdAmount(params);
    };
    remainingUsdAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    remainingUsdAmtCd.columnFilterData = null;
    if (!this.restoreFromConfig(remainingUsdAmtCd)) {
      remainingUsdAmtCd.hide = true;
      remainingUsdAmtCd.position = this.position.next();
    }
    return remainingUsdAmtCd;
  }
  createRemainingAmtColumn(title: string): BfxColDef {
    const remainingAmt: BfxColDef = this.initColdDef();
    remainingAmt.headerName = title;
    remainingAmt.field = OrdersTableDefinitionFactoryConstants.COLUMN_REMAINING_AMOUNT;
    remainingAmt.indexType = IndexType.NaturalString;
    remainingAmt.minWidth = 90;
    remainingAmt.width = this.eightWs;
    remainingAmt.isPriority = true;
    remainingAmt.cellClass = ["blotter_amt_align blotter_priority_col"];
    remainingAmt.columnFilterType = ColumnFilterType.NUMBER;
    remainingAmt.columnFilterData = null;
    remainingAmt.valueGetter = function (this: null, params) {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (order != null && order.orderType == CubiqTS.OrderType.LIMIT_CALL) {
        return null;
      }

      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          return params.data[OrdersTableDefinitionFactoryConstants.COLUMN_REMAINING_AMOUNT];
        case OrdersTableMode.BASE:
        case OrdersTableMode.NDF_BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            if (params.data.baseCode != params.data.fixedCode) {
              return params.data.leavesQty;
            }
            return params.data.leavesSettleQty;
          }
          if (params.data.leavesQty && params.data.baseCode == params.data.fixedCode) {
            return params.data.leavesQty;
          } else if (params.data.leavesSettleQty) {
            return params.data.leavesSettleQty;
          }
          return null;
      }
    };
    remainingAmt.valueFormatter = (params) => {
      let order: CubiqTS.OrderDataImpl = params.data;
      let amountString;

      if (CubiqTS.RequestOrderType.BMARK === order.requestedOrderType) {
        let riskData: BmarkRiskData = params.data.risk;
        if (null != riskData && riskData.isSwap) {
          const isFilled =
            order.orderState === CubiqTS.OrderState.FILLED ||
            order.orderState === CubiqTS.OrderState.PENDING_FWDS ||
            order.orderState === CubiqTS.OrderState.EXPIRY_PENDING_FWDS ||
            order.orderState === CubiqTS.OrderState.CANCEL_PENDING_FWDS;
          amountString = this.orderAmountHelper.formatAmount(
            riskData.fixedCcy,
            riskData.riskAmount,
            riskData.isSwap,
            isFilled ? ZERO : riskData.nearAmount,
            isFilled ? ZERO : riskData.farAmount,
          );
        }
      }
      if (!amountString) {
        amountString = this.formatCcyAmount(order.fixedCode, params.value);
      }
      return amountString;
    };
    remainingAmt.tooltipValueGetter = (params) => {
      let order: CubiqTS.OrderDataImpl = params.data;
      if (CubiqTS.RequestOrderType.BMARK === order.requestedOrderType) {
        let riskData: BmarkRiskData = params.data.risk;
        if (null != riskData && riskData.isSwap) {
          const isFilled =
            order.orderState === CubiqTS.OrderState.FILLED ||
            order.orderState === CubiqTS.OrderState.PENDING_FWDS ||
            order.orderState === CubiqTS.OrderState.EXPIRY_PENDING_FWDS ||
            order.orderState === CubiqTS.OrderState.CANCEL_PENDING_FWDS;
          return this.orderAmountHelper.formatAmountTooltip(
            isFilled ? ZERO : riskData.riskAmount,
            riskData.isSwap,
            isFilled ? ZERO : riskData.nearAmount,
            isFilled ? ZERO : riskData.farAmount,
            riskData?.fixedCcy,
          );
        }
      }
      return params.valueFormatted;
    };

    if (!this.restoreFromConfig(remainingAmt)) {
      remainingAmt.position = this.position.next();
    }
    return remainingAmt;
  }
  createUSDAmtColumn(title: string): BfxColDef {
    const usdAmtCd: BfxColDef = this.initColdDef();
    usdAmtCd.headerName = title;
    usdAmtCd.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_USD_AMOUNT;
    usdAmtCd.indexType = IndexType.NaturalString;
    usdAmtCd.minWidth = this.oneWs;
    usdAmtCd.width = this.eightWs;
    usdAmtCd.cellClass = ["blotter_amt_align"];
    usdAmtCd.valueFormatter = (params: ValueFormatterParams) => {
      return this.formatUsdAmount(params);
    };
    usdAmtCd.columnFilterType = ColumnFilterType.NUMBER;
    usdAmtCd.columnFilterData = null;
    usdAmtCd.valueGetter = (params: ValueGetterParams) => {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (order == null || order.orderType == CubiqTS.OrderType.LIMIT_CALL) {
        return null;
      }

      return order[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_USD_AMOUNT];
    };

    if (!this.restoreFromConfig(usdAmtCd)) {
      usdAmtCd.hide = true;
      usdAmtCd.position = this.position.next();
    }
    return usdAmtCd;
  }
  createDealtCCYColumn(title: string): BfxColDef {
    let dealtCcy: BfxColDef = this.initColdDef();
    dealtCcy.headerName = title;
    dealtCcy.field = OrdersTableDefinitionFactoryConstants.COLUMN_DEALT_CURRENCY;
    dealtCcy.indexType = IndexType.String;
    dealtCcy.minWidth = this.oneWs;
    dealtCcy.width = this.threeWs;
    dealtCcy.valueGetter = function (this: null, params) {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (order != null && order.orderType == CubiqTS.OrderType.LIMIT_CALL) {
        return EMPTY_TEXT_VALUE;
      }

      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          return params.data[OrdersTableDefinitionFactoryConstants.COLUMN_DEALT_CURRENCY];
        case OrdersTableMode.BASE:
        case OrdersTableMode.NDF_BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            return params.data[OrdersTableDefinitionFactoryConstants.COLUMN_INVERT_CURRENCY];
          }
          return params.data[OrdersTableDefinitionFactoryConstants.COLUMN_BASE_CURRENCY];
      }
    };
    dealtCcy.cellClass = ["blotter_center_align_col"];
    dealtCcy.columnFilterType = ColumnFilterType.NONE;
    if (!this.restoreFromConfig(dealtCcy)) {
      dealtCcy.position = this.position.next();
    }
    return dealtCcy;
  }

  createInstrumentColumn(title: string): BfxColDef {
    let instrumentCol: BfxColDef = this.initColdDef();
    instrumentCol.headerName = title;
    instrumentCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_INSTRUMENT;
    instrumentCol.indexType = IndexType.String;
    instrumentCol.alias = [COLUMN_TRADE_INSTRUMENT, COLUMN_INTER_INSTRUMENT];
    instrumentCol.minWidth = this.oneWs;
    instrumentCol.width = this.sixWs;
    instrumentCol.hide = false;
    instrumentCol.omitFromPicker = false;
    instrumentCol.cellClass = ["blotter_left_align_col"];
    instrumentCol.columnFilterType = ColumnFilterType.CURRENCY_PAIR;
    instrumentCol.valueGetter = function (this: null, params) {
      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          if (params.data["isInvertedOrderFg"]) {
            return params.data["nonBaseCode"] + "/" + params.data["baseCode"];
          }
          return params.data["baseCode"] + "/" + params.data["nonBaseCode"];
        case OrdersTableMode.NDF_BASE:
        case OrdersTableMode.BASE:
          return (
            params.data["baseCode"] +
            "/" +
            params.data["nonBaseCode"] +
            (params.data.isInvertedOrderFg ? `&nbsp;<sup>1</sup>/<sub>n</sub>` : "")
          );
      }
      return EMPTY_TEXT_VALUE;
    };
    instrumentCol.getFilterValue = function (this: OrdersTableDefinitionFactory, params) {
      return params.baseCode + "/" + params.nonBaseCode;
    };
    instrumentCol.cellRenderer = function (this: OrdersTableDefinitionFactory, params) {
      switch (params.context.mode) {
        case OrdersTableMode.NDF_BASE:
        case OrdersTableMode.BASE:
          if (params.data.isInvertedOrderFg) {
            return params.value;
          }
          break;
      }
      return params.value;
    };
    instrumentCol.tooltipValueGetter = function (this: null, params) {
      if (Object.prototype.hasOwnProperty.call(params, "rowIndex") == false)
        return params.colDef.headerName;

      switch (params.context.mode) {
        case OrdersTableMode.NDF_BASE:
        case OrdersTableMode.BASE:
          if (params.data.isInvertedOrderFg) {
            return `${params.data["baseCode"]}/${params.data["nonBaseCode"]} ¹⁄ₙ`;
          }
          break;
      }
      return params.value;
    };
    instrumentCol.getQuickFilterText = function (this: null, params) {
      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          if (params.data["isInvertedOrderFg"]) {
            return params.data["nonBaseCode"] + "/" + params.data["baseCode"];
          }
          return params.data["baseCode"] + "/" + params.data["nonBaseCode"];
        case OrdersTableMode.NDF_BASE:
        case OrdersTableMode.BASE:
          return params.data["baseCode"] + "/" + params.data["nonBaseCode"];
      }
      return params.data.baseCode + "/" + params.data.nonBaseCode;
    };

    if (!this.restoreFromConfig(instrumentCol)) {
      instrumentCol.position = this.position.next();
    }

    return instrumentCol;
  }

  createAccountIdColumn(title: string): BfxColDef {
    let accId: BfxColDef = this.initColdDef();
    accId.headerName = title;
    accId.field = OrdersTableDefinitionFactoryConstants.COLUMN_ACCOUNT_CODE;
    accId.indexType = IndexType.String;
    accId.minWidth = this.oneWs;
    accId.width = this.tenWs;
    accId.columnFilterType = ColumnFilterType.ACCOUNTID;
    accId.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      if (params?.data?.orderType === CubiqTS.OrderType.BENCHMARK) {
        if (StaticDataPrivatizationType.hasPrivatization(params.data)) {
          return this.privatizationHelper.getPrivatizedValue(
            params,
            OrdersTableDefinitionFactoryConstants.COLUMN_ACCOUNT_CODE,
          );
        }

        const risk: BmarkRiskData = params != null && params.data != null ? params.data.risk : null;

        if (risk != null && risk.isSingleAccount) {
          return risk.firstAccountCode;
        } else if (!risk && params.data.accountCode) {
          // If this is a find we have no risk but query will never have account for multiple account BM
          return params.data.accountCode;
        }

        return "Multiple";
      }

      return this.privatizationHelper.getPrivatizedValue(
        params,
        OrdersTableDefinitionFactoryConstants.COLUMN_ACCOUNT_CODE,
      );
    }.bind(this);
    if (!this.restoreFromConfig(accId)) {
      accId.hide = true;
      accId.position = this.position.next();
    }
    return accId;
  }

  createAccountNameColumn(title: string): BfxColDef {
    let accName: BfxColDef = this.initColdDef();
    accName.headerName = title;
    accName.field = OrdersTableDefinitionFactoryConstants.COLUMN_ACCOUNT_NAME;
    accName.indexType = IndexType.String;
    accName.minWidth = this.oneWs;
    accName.width = this.tenWs;
    accName.columnFilterType = ColumnFilterType.ACCOUNT;
    accName.getFilterValue = (dataEntity: any) => {
      return this.privatizationHelper.getPrivatizedValue(
        { data: dataEntity },
        OrdersTableDefinitionFactoryConstants.COLUMN_ACCOUNT_CODE,
      );
    };
    accName.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      if (params?.data?.orderType === CubiqTS.OrderType.BENCHMARK) {
        if (StaticDataPrivatizationType.hasPrivatization(params.data)) {
          // Privatised value is in the stored Account id/name property - getting name will not do lookup
          return this.privatizationHelper.getPrivatizedValue(
            params,
            OrdersTableDefinitionFactoryConstants.COLUMN_ACCOUNT_NAME,
          );
        }

        const risk: BmarkRiskData = params != null && params.data != null ? params.data.risk : null;

        if (risk != null && risk.isSingleAccount) {
          return risk.firstAccountName;
        } else if (!risk && params.data.accountName) {
          // If this is a find we have no risk but query will never have account for multiple account BM
          return params.data.accountName;
        }

        return "Multiple";
      }

      return this.privatizationHelper.getPrivatizedValue(
        params,
        OrdersTableDefinitionFactoryConstants.COLUMN_ACCOUNT_NAME,
      );
    }.bind(this);
    if (!this.restoreFromConfig(accName)) {
      accName.hide = true;
      accName.position = this.position.next();
    }

    return accName;
  }

  createClientIdColumn(title: string): BfxColDef {
    let clientId: BfxColDef = this.initColdDef();
    clientId.headerName = title;
    clientId.field = OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_CODE;
    clientId.indexType = IndexType.String;
    clientId.minWidth = this.oneWs;
    clientId.width = this.tenWs;
    clientId.columnFilterType = ColumnFilterType.CLIENTID;
    clientId.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return this.privatizationHelper.getPrivatizedValue(
        params,
        OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_CODE,
      );
    }.bind(this);
    if (!this.restoreFromConfig(clientId)) {
      clientId.hide = true;
      clientId.position = this.position.next();
    }
    return clientId;
  }

  createClientNameColumn(title: string): BfxColDef {
    let clNm: BfxColDef = this.initColdDef();
    clNm.headerName = title;
    clNm.field = OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_NAME;
    clNm.indexType = IndexType.String;
    clNm.minWidth = this.oneWs;
    clNm.width = this.tenWs;
    clNm.columnFilterType = ColumnFilterType.CLIENT;
    clNm.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return this.privatizationHelper.getPrivatizedValue(
        params,
        OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_NAME,
      );
    }.bind(this);
    clNm.getFilterValue = function (this: OrdersTableDefinitionFactory, params) {
      return this.privatizationHelper.getPrivatizedValue(
        { data: params },
        OrdersTableDefinitionFactoryConstants.COLUMN_CLIENT_CODE,
      );
    }.bind(this);
    clNm.cellClass = ["blotter_left_align_col"];
    if (!this.restoreFromConfig(clNm)) {
      clNm.position = this.position.next();
    }
    return clNm;
  }

  createOrderStateColumn(title: string): BfxColDef {
    let orderStateColumn: BfxColDef = this.initColdDef();
    orderStateColumn.headerName = title;
    orderStateColumn.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_STATE;
    orderStateColumn.indexType = IndexType.String;
    orderStateColumn.minWidth = this.oneWs;
    orderStateColumn.width = this.sevenWs;
    orderStateColumn.columnFilterType = ColumnFilterType.ORDERSTATE;
    orderStateColumn.columnFilterData = OrderBlotterFilterOrderStates;
    orderStateColumn.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return this.myOrderStateHelper.getState(params);
    }.bind(this);
    orderStateColumn.cellClass = ["blotter_left_align_col"];
    if (!this.restoreFromConfig(orderStateColumn)) {
      orderStateColumn.position = this.position.next();
    }
    return orderStateColumn;
  }

  // acts on settleTenor as well as displaying label
  createValueDtColumn(title: string): BfxColDef {
    let vd: BfxColDef = this.initColdDef();
    vd.headerName = title;
    vd.field = OrdersTableDefinitionFactoryConstants.COLUMN_VALUE_DATE;
    vd.alias = [COLUMN_TRADE_VALUE_DATE];
    vd.minWidth = this.oneWs;
    vd.width = this.sixWs;
    vd.sortKey = "valueDateSort";
    vd.indexType = IndexType.AgGridDefault;
    vd.columnFilterType =
      this.myFwdEnabled || this.myNdfEnabled ? ColumnFilterType.DATE_TENOR : ColumnFilterType.NONE;
    vd.columnFilterData = this.myFwdEnabled || this.myNdfEnabled ? CubiqTS.Tenor : null;
    vd.valueGetter = (params) => {
      return this.myValueDateHelper.getMoment(params.data);
    };
    vd.valueFormatter = (params) => {
      const risk: BmarkRiskData = params.data.risk;
      return ValueDateRenderer.render(
        params.value,
        params.data.settleTenor,
        risk ? risk.isSwap : false,
        risk ? risk.isSSP : false,
        risk ? risk.nearTenor : null,
        risk ? risk.nearValueDate : null,
        risk ? risk.farTenor : null,
        risk ? risk.farValueDate : null,
        this.myValueDateHelper,
      );
    };
    vd.comparator = (valueA, valueB, nodeA, nodeB, isInverted) => {
      const order1: CubiqTS.OrderData = nodeA.data as CubiqTS.OrderData;
      const order2: CubiqTS.OrderData = nodeB.data as CubiqTS.OrderData;
      const pair1 = order1.baseCode + order1.nonBaseCode;
      const pair2 = order2.baseCode + order2.nonBaseCode;
      if (pair1 != pair2) {
        return pair1.localeCompare(pair2);
      }
      const mom1 = valueA as CubiqTS.Moment;
      const mom2 = valueB as CubiqTS.Moment;
      if (valueA == null && valueB == null) {
        return 0;
      }
      if (valueA == null) {
        return 1;
      }
      if (valueB == null) {
        return -1;
      }

      return mom1.isSame(mom2) ? 0 : mom1.isAfter(mom2) ? 1 : -1;
    };

    if (!this.restoreFromConfig(vd)) {
      vd.hide = true;
      vd.position = this.position.next();
    }

    return vd;
  }

  createOrderAmtColumn(title: string): BfxColDef {
    let orderAmtCd: BfxColDef = this.initColdDef();
    orderAmtCd.headerName = title;
    orderAmtCd.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_AMOUNT;
    orderAmtCd.indexType = IndexType.NaturalString;
    orderAmtCd.minWidth = 90;
    orderAmtCd.width = this.eightWs;
    orderAmtCd.isPriority = true;
    orderAmtCd.cellClass = ["blotter_amt_align"];
    orderAmtCd.valueGetter = (params) => {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (order != null && CubiqTS.OrderType.LIMIT_CALL == order.orderType) {
        return null;
      }

      switch (params.context.mode) {
        case OrdersTableMode.DEALT:
        case OrdersTableMode.NDF_DEALT:
          return params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_AMOUNT];
        case OrdersTableMode.BASE:
        case OrdersTableMode.NDF_BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            if (params.data.baseCode != params.data.fixedCode) {
              return params.data.orderQty;
            }
            return params.data.settleQty;
          }
          if (params.data.baseCode == params.data.fixedCode) {
            return params.data.orderQty;
          }
          return params.data.settleQty;
      }
    };

    orderAmtCd.valueFormatter = (params) => {
      let order: CubiqTS.OrderDataImpl = params.data;
      let amountString: string;

      if (CubiqTS.RequestOrderType.BMARK === order.requestedOrderType) {
        let riskData: BmarkRiskData = params.data.risk;
        if (null != riskData) {
          amountString = this.orderAmountHelper.formatAmount(
            riskData.fixedCcy,
            riskData.riskAmount,
            riskData.isSwap,
            riskData.nearAmount,
            riskData.farAmount,
          );
        }
      }
      if (!amountString) {
        amountString = this.formatCcyAmount(order.fixedCode, params.value);
      }
      return amountString;
    };
    orderAmtCd.tooltipValueGetter = (params) => {
      if (params?.data?.risk) {
        const risk: BmarkRiskData = params.data.risk;
        if (risk) {
          return this.orderAmountHelper.formatAmountTooltip(
            risk.riskAmount,
            risk.isSwap,
            risk.nearAmount,
            risk.farAmount,
            risk.fixedCcy,
          );
        }
      }
      return params.valueFormatted;
    };

    orderAmtCd.columnFilterType = ColumnFilterType.INT;
    orderAmtCd.columnFilterData = null;
    if (!this.restoreFromConfig(orderAmtCd)) {
      orderAmtCd.position = this.position.next();
    }

    return orderAmtCd;
  }

  createProductColumn(title: string): BfxColDef {
    let prodCol: BfxColDef = this.initColdDef();
    prodCol.headerName = title;
    prodCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_PRODUCT;
    prodCol.alias = [COLUMN_TRADE_ORDER_PRODUCT, COLUMN_INTER_ORDER_PRODUCT];
    prodCol.indexType = IndexType.String;
    prodCol.cellClass = ["blotter_left_align_col"];
    prodCol.minWidth = this.oneWs;
    prodCol.width = this.fiveWs;
    prodCol.columnFilterType = ColumnFilterType.ENUM;
    prodCol.columnFilterData = CubiqTS.Product;
    prodCol.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return ProductRenderer.render(
        params.data.product,
        params.data.requestTrackingType,
        params.data.orderType,
      );
    };
    if (!this.restoreFromConfig(prodCol)) {
      prodCol.position = this.position.next();
    }

    return prodCol;
  }

  createIntentColumn(title: string): BfxColDef {
    let intentCol: BfxColDef = this.initColdDef();

    intentCol.headerName = title;
    intentCol.field = OrdersTableDefinitionFactoryConstants.COLUMN_DEALT_DIR;
    intentCol.indexType = IndexType.String;
    intentCol.minWidth = this.twoWs;
    intentCol.width = this.fiveWs;
    intentCol.cellRenderer = "intentRenderer";
    intentCol.cellClass = ["blotter_center_align_col intent_col"];
    intentCol.columnFilterType = ColumnFilterType.NONE;
    intentCol.columnFilterData = CubiqTS.OrderIntent;
    intentCol.tooltipValueGetter = (params) => {
      return params.value;
    };

    intentCol.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      const orderType: CubiqTS.RequestOrderType = params.data.requestedOrderType;
      const product: CubiqTS.Product = params.data.product;
      const fixedCode: string = params.data.fixedCode;

      let clientIntent: CubiqTS.OrderIntent = params.data.clientIntent;

      const invertedOrder: boolean =
        params.context.isInverted && params.context.isInverted(params.data);

      let modeFlip: boolean = false;
      const mode: OrdersTableMode = params.context.mode;
      if (
        (OrdersTableMode.BASE == mode || OrdersTableMode.NDF_BASE == mode) &&
        fixedCode !== params.data.baseCode
      ) {
        modeFlip = true;
      }

      if ((invertedOrder && !modeFlip) || (modeFlip && !invertedOrder)) {
        clientIntent =
          clientIntent == CubiqTS.OrderIntent.BUY
            ? CubiqTS.OrderIntent.SELL
            : CubiqTS.OrderIntent.BUY;
      }

      let risk: BmarkRiskData = params?.data?.risk;
      return ProductIntentRenderer.render(
        clientIntent,
        fixedCode,
        product,
        orderType,
        params.data.allocationData,
        risk,
      );
    };

    if (!this.restoreFromConfig(intentCol)) {
      intentCol.position = this.position.next();
    }

    return intentCol;
  }

  protected createStartegyParamsColumn(): BfxColDef {
    // Strategy Params
    let strParameters: BfxColDef = this.initColdDef();
    strParameters.headerName = "Strategy Params";
    strParameters.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_STRATEGY_PARAMETERS;
    strParameters.sortingOrder = [];
    strParameters.minWidth = this.oneWs;
    strParameters.width = this.nineWs;
    strParameters.columnFilterType = ColumnFilterType.NONE;
    strParameters.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (
        (CubiqTS.RequestOrderType.ALGO !== order.requestedOrderType ||
          typeof order.algoStrategyName !== "string") &&
        order.orderType !== CubiqTS.OrderType.LIMIT_TP &&
        order.orderType !== CubiqTS.OrderType.LIMIT_SL &&
        order.orderType !== CubiqTS.OrderType.BMALGO
      ) {
        return EMPTY_TEXT_VALUE;
      }

      let value = "";
      let strategyParameters = order.strategyParameters;
      if (strategyParameters != null && Array.isArray(strategyParameters)) {
        strategyParameters.sort(
          (a: CubiqTS.StrategyParameterImpl, b: CubiqTS.StrategyParameterImpl) => {
            return a.parameterName.localeCompare(b.parameterName);
          },
        );

        this.atdlTooltipUiRep.inlineUiRep(order.algoStrategyName, strategyParameters);

        for (const [i, strategyParameter] of strategyParameters.entries()) {
          value += `${strategyParameter.parameterName}: ${strategyParameter.parameterValue}`;

          if (i < strategyParameters.length - 1) value += `; `;
        }
      }

      return value;
    }.bind(this);
    strParameters.tooltipValueGetter = MultiLineTooltipValueGetter;
    if (!this.restoreFromConfig(strParameters)) {
      strParameters.hide = true;
      strParameters.position = this.position.next();
    }

    return strParameters;
  }

  protected getMarketBar(td: TableDefinition): any {
    let bar = {};
    let id = CubiqTS.Long.fromNumber(OrdersTableDefinitionFactoryConstants.MARKET_BAR_ORDER_ID_KEY);
    bar[td.primaryKey] = id.toString();
    bar[OVERRIDE_KEY] = id;
    bar["isMarketBar"] = true;
    bar["percSort"] = 0;
    bar["perc"] = 0;
    bar["tempStale"] = false;
    bar["fullWidth"] = true;

    return bar;
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  public getAssetClass(): CubiqTS.AssetClass {
    return CubiqTS.AssetClass.FIX;
  }

  public getDefaultDealtMode(): any {
    return OrdersTableMode.isDealt(this.mode);
  }

  public getDefaultNdfMode(): any {
    return OrdersTableMode.isNdf(this.mode);
  }

  public getBlotterMode() {
    return this.mode;
  }

  public isDealtMode() {
    return this.mode == OrdersTableMode.DEALT || this.mode == OrdersTableMode.NDF_DEALT;
  }

  public getVersionIdentifier(): string {
    return ORDERS_TABLE_DEFINITION_VER;
  }

  private mapUserSettings(profileValue: any) {
    if (profileValue) {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != undefined && savedSettings != null) {
        this.marketBarEnabled =
          savedSettings.hasOwnProperty("marketBar") &&
          savedSettings.marketBar !== null &&
          savedSettings.marketBar == "y"
            ? true
            : false;
      }
    }
  }

  private getFixingTimeValue(order: any, convertToUTC: boolean, fullDateTimeUTC: boolean): string {
    let fixingDate: CubiqTS.Moment =
      order[OrdersTableDefinitionFactoryConstants.FIXING_DATE_COLUMN_PROPERTY];
    let reqFixingTimeProperty = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      OrdersTableDefinitionFactoryConstants.REQUESTED_FIXING_TIME_COLUMN_PROPERTY,
    );
    let reqFixingDateProperty = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      OrdersTableDefinitionFactoryConstants.REQUESTED_FIXING_DATE_COLUMN_PROPERTY,
    );
    let reqTimeZoneCodeProperty: any = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      OrdersTableDefinitionFactoryConstants.REQUESTED_FIXING_TIMEZONE_COLUMN_PROPERTY,
    );
    let reqFormattedDateProperty: string = CubiqTS.moment(reqFixingDateProperty).format(
      OrdersTableDefinitionFactoryConstants.DATE_FORMAT_SHORT,
    );
    let reqFixingDateFull: string = FixingTimeAdapter.transformDateStringToMomentFormatString(
      reqFixingDateProperty,
      reqFixingTimeProperty,
    );

    if (order.orderType === CubiqTS.OrderType.BENCHMARK) {
      if (convertToUTC) {
        if (fullDateTimeUTC) {
          reqFormattedDateProperty = CubiqTS.moment
            .utc(fixingDate)
            .format(OrdersTableDefinitionFactoryConstants.DATE_FORMAT_SHORT);
          return (
            CubiqTS.moment
              .utc(fixingDate)
              .format(OrdersTableDefinitionFactoryConstants.TIME_FORMAT_SHORT) +
            " " +
            OrdersTableDefinitionFactoryConstants.TIME_ZONE_UTC +
            " " +
            reqFormattedDateProperty
          );
        }
        return CubiqTS.moment
          .utc(fixingDate)
          .format(OrdersTableDefinitionFactoryConstants.TIME_FORMAT_SHORT);
      }

      if (reqFixingDateFull) {
        return (
          CubiqTS.moment
            .utc(reqFixingDateFull)
            .format(OrdersTableDefinitionFactoryConstants.TIME_FORMAT_SHORT) +
          " " +
          reqTimeZoneCodeProperty +
          " " +
          reqFormattedDateProperty
        );
      }
    } else {
      return CubiqTS.moment(fixingDate)
        .utc()
        .format(OrdersTableDefinitionFactoryConstants.DATE_FORMAT_SHORT);
    }
    return EMPTY_TEXT_VALUE;
  }

  private getPosition(columnData: string, columnDefs: Array<ColumnDefinition>): number {
    let pos: number = 0;
    for (let column of columnDefs) {
      if (columnData == column.data) {
        pos = column.position;
        break;
      }
    }
    return pos;
  }

  protected goodUntilFormatter(params: ValueFormatterParams): string {
    let expiryType: CubiqTS.OrderExpiryType = params.data.expiryType;

    if (expiryType == CubiqTS.OrderExpiryType.GTC) {
      return "GTC";
    }

    if (params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXPIRY_DATE]) {
      return this.formatMomment(params);
    }

    return "";
  }

  protected goodFromFormatter(params: ValueFormatterParams): string {
    if (params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_ACTIVATION_DATE]) {
      return this.formatMomment(params);
    } else if (params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_ENTRY_DATE]) {
      return params.data[OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_ENTRY_DATE]
        .local()
        .format(this.localeString);
    }

    return "";
  }

  public formatRate(params: ValueFormatterParams) {
    if (params.value == null) {
      return EMPTY_AMT_RATE_VALUE;
    }

    let value: any = params.value;
    if (typeof params.value == "string") {
      value = new CubiqTS.BigNumber(params.value);
    }

    let minPrecision: number = 0;
    let maxPrecision: number = 0;

    let order: CubiqTS.OrderDataImpl = params.data;

    let base: string = order.baseCode;
    let nonBase: string = order.nonBaseCode;
    let dealt: boolean =
      params.context.mode == OrdersTableMode.DEALT ||
      params.context.mode == OrdersTableMode.NDF_DEALT;

    let ccyPair: CubiqTS.CurrencyPairImpl = this.ccyPairLookupService.getCcyPair(base, nonBase);

    if (ccyPair) {
      if (order.isInvertedOrderFg && dealt) {
        minPrecision = ccyPair.spotInvertDecimalsQty;
        maxPrecision = ccyPair.fwdInvertDecimalsQty;
      } else {
        minPrecision = ccyPair.pipsQty;
        maxPrecision = ccyPair.fwdRateDecimalsQty;
      }
    }

    let currentDecs: number = value.decimalPlaces();
    if (currentDecs < minPrecision) {
      return value.toFixed(minPrecision);
    } else if (currentDecs > maxPrecision) {
      return value.toFixed(maxPrecision);
    }

    return value.toFixed(currentDecs);
  }

  async getBmarkOrderDisplayName() {
    this.bmarkShortNm = await this.getBMShortNameFromSysConfig();
  }

  private getBMShortNameFromSysConfig() {
    if (this.sysConfigService) {
      return new Promise<string>((res, rej) => {
        this.sysConfigService
          .getSystemConfig(SysFeatures.fixingOrderShortName)
          .then((value) => {
            let returnValue = value != null && value.length > 0 ? value : BMARK_DEFAULT_NAME;
            res(returnValue);
          })
          .catch((error) => {
            logger.out.error(`OrdersTableDefinitionFactory : getSystemConfig :
                        failed to get system config for: ${SysFeatures.fixingOrderShortName}`);
            rej(error.errorMessages);
          });
      });
    }
  }

  protected formatAmtsExp(params: ValueFormatterParams): string {
    if (params.value) {
      if (params.value.gte(1e10)) {
        return params.value.toExponential(3);
      }
      return params.value.toFormat(0);
    }
    return EMPTY_AMT_RATE_VALUE;
  }

  /**
   * Format the cparameter value as a USD amount
   * @param params
   * @returns
   */
  protected formatUsdAmount(params: ValueFormatterParams): string {
    return this.formatCcyAmount("USD", params.value);
  }

  /**
   * Format an amount using the correct precision of the currencies minor units precision.
   * By default this has no precision but can be overridden e.g. in Find Orders
   * @param currency
   * @param amt
   * @returns
   */
  protected formatCcyAmount(_currency: string, amt: CubiqTS.BigNumber) {
    let result: string = null;

    if (amt) {
      result = amt.toFormat(0);
    } else {
      result = EMPTY_AMT_RATE_VALUE;
    }

    return result;
  }

  /**
   * Add algo specific columns to the table definition. Invoked
   * by subclasses suporting algos, algo monitor and find orders
   * in particular.
   *
   * @param td the table definition to add algo columns to
   */
  protected addAlgoSpecificColumns(td: TableDefinition): void {
    td.addColumn(this.createGoodFromColumn("Good From"));

    // If table definition doesn't provide the volume per minute service, skip this column
    if (this.enabledVolumePerMinute === true) {
      td.addColumn(this.createVolPerMinColumn("Vol/Min"));
    }

    td.addColumn(this.createAvgMktSpotColumn("Mkt Spot (Avg)"));

    td.addColumn(this.createAvgClientSpotColumn("Client Spot (Avg)"));

    td.addColumn(this.createAvgMktPtsColumn("Mkt Pts (Avg)"));

    td.addColumn(this.createAvgClientPtsColumn("Client Pts (Avg)"));

    td.addColumn(this.createAvgMktAllinColumn("Mkt All-In (Avg)"));
  }

  createVolPerMinColumn(title: string): BfxColDef {
    const volumePerMinuteColumn: BfxColDef = this.initColdDef();
    volumePerMinuteColumn.headerName = title;
    volumePerMinuteColumn.field = OrdersTableDefinitionFactoryConstants.COLUMN_VOL_PER_MIN;
    volumePerMinuteColumn.indexType = IndexType.NaturalString;
    volumePerMinuteColumn.minWidth = this.oneWs;
    volumePerMinuteColumn.width = this.eightWs;
    volumePerMinuteColumn.isPriority = true;
    volumePerMinuteColumn.cellClass = ["blotter_amt_align"];
    volumePerMinuteColumn.columnFilterType = ColumnFilterType.NUMBER;
    volumePerMinuteColumn.columnFilterData = null;
    volumePerMinuteColumn.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      let order: CubiqTS.OrderDataImpl = params.data;

      if (order != null && !this.volumePerMinuteHelper.isSupportedOrder(order)) {
        return null;
      }

      let amount: CubiqTS.BigNumber =
        params.data[OrdersTableDefinitionFactoryConstants.COLUMN_REMAINING_AMOUNT];

      switch (params.context.mode) {
        case OrdersTableMode.BASE:
        case OrdersTableMode.NDF_BASE:
          if (params.context.isInverted && params.context.isInverted(params.data)) {
            if (params.data.baseCode != params.data.fixedCode) {
              amount = params.data.leavesQty;
            } else {
              amount = params.data.leavesSettleQty;
            }
          } else {
            if (params.data.leavesQty && params.data.baseCode == params.data.fixedCode) {
              amount = params.data.leavesQty;
            } else if (params.data.leavesSettleQty) {
              amount = params.data.leavesSettleQty;
            } else {
              amount = null;
            }
          }
      }

      if (amount != null) {
        amount = this.volumePerMinuteHelper.getVolumePerMinuteAmount(amount, order);
      }

      return amount;
    }.bind(this);
    volumePerMinuteColumn.valueFormatter = this.formatAmtsExp;
    if (!this.restoreFromConfig(volumePerMinuteColumn)) {
      volumePerMinuteColumn.position = this.position.next();
    }
    return volumePerMinuteColumn;
  }

  createGoodFromColumn(title: string): BfxColDef {
    let activationDt: BfxColDef = this.initColdDef();
    activationDt.headerName = title;
    activationDt.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_ACTIVATION_DATE;
    activationDt.valueGetter = (params: ValueGetterParams) => {
      const order = params.data as CubiqTS.OrderDataImpl;
      const retValue = order?.activationDts ?? order?.entryDts ?? null;
      return retValue;
    };
    activationDt.valueFormatter = this.goodFromFormatter.bind(this);
    activationDt.minWidth = this.oneWs;
    activationDt.width = this.fiveWs;
    activationDt.columnFilterType = ColumnFilterType.NONE;

    // Do not export as good from is already exported as 'selection' column
    activationDt.skipExport = true;

    if (!this.restoreFromConfig(activationDt)) {
      activationDt.position = this.position.next();
    }
    return activationDt;
  }
  createAvgMktAllinColumn(title: string): BfxColDef {
    let avgMarketAllIn: BfxColDef = this.initColdDef();
    avgMarketAllIn.headerName = title;
    avgMarketAllIn.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_MKT_VWAP_ALL_IN;
    avgMarketAllIn.indexType = IndexType.NaturalString;
    avgMarketAllIn.cellClass = [];
    avgMarketAllIn.minWidth = this.oneWs;
    avgMarketAllIn.cellClass = ["blotter_center_align_numeric"];
    avgMarketAllIn.width = this.fourWs;
    avgMarketAllIn.columnFilterType = ColumnFilterType.NONE;
    avgMarketAllIn.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return this.myDealtPricingService.getVwap(params.data, params.colDef.field);
    }.bind(this);

    if (!this.restoreFromConfig(avgMarketAllIn)) {
      avgMarketAllIn.hide = true;
      avgMarketAllIn.position = this.position.next();
    }
    return avgMarketAllIn;
  }

  createAvgClientPtsColumn(title: string): BfxColDef {
    let avgClientPoints: BfxColDef = this.initColdDef();
    avgClientPoints.headerName = title;
    avgClientPoints.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_VWAP_PTS;
    avgClientPoints.indexType = IndexType.NaturalString;
    avgClientPoints.cellClass = [];
    avgClientPoints.minWidth = this.oneWs;
    avgClientPoints.cellClass = ["blotter_center_align_numeric"];
    avgClientPoints.width = this.fourWs;
    avgClientPoints.columnFilterType = ColumnFilterType.NONE;
    avgClientPoints.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return this.myDealtPricingService.getClientVwapPoints(params.data);
    }.bind(this);

    if (!this.restoreFromConfig(avgClientPoints)) {
      avgClientPoints.hide = true;
      avgClientPoints.position = this.position.next();
    }
    return avgClientPoints;
  }
  createAvgMktPtsColumn(title: string): BfxColDef {
    let avgMarketPoints: BfxColDef = this.initColdDef();
    avgMarketPoints.headerName = title;
    avgMarketPoints.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_MKT_VWAP_PTS;
    avgMarketPoints.indexType = IndexType.NaturalString;
    avgMarketPoints.cellClass = [];
    avgMarketPoints.minWidth = this.oneWs;
    avgMarketPoints.cellClass = ["blotter_center_align_numeric"];
    avgMarketPoints.width = this.fourWs;
    avgMarketPoints.columnFilterType = ColumnFilterType.NONE;
    avgMarketPoints.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return this.myDealtPricingService.getMarketVwapPoints(params.data);
    }.bind(this);

    if (!this.restoreFromConfig(avgMarketPoints)) {
      avgMarketPoints.hide = true;
      avgMarketPoints.position = this.position.next();
    }
    return avgMarketPoints;
  }
  createAvgClientSpotColumn(title: string): BfxColDef {
    let avgClientSpot: BfxColDef = this.initColdDef();
    avgClientSpot.headerName = title;
    avgClientSpot.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_VWAP_SPOT;
    avgClientSpot.indexType = IndexType.NaturalString;
    avgClientSpot.cellClass = [];
    avgClientSpot.minWidth = this.oneWs;
    avgClientSpot.cellClass = ["blotter_center_align_numeric"];
    avgClientSpot.width = this.fourWs;
    avgClientSpot.columnFilterType = ColumnFilterType.NONE;
    avgClientSpot.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return this.myDealtPricingService.getVwap(params.data, params.colDef.field);
    }.bind(this);

    if (!this.restoreFromConfig(avgClientSpot)) {
      avgClientSpot.hide = true;
      avgClientSpot.position = this.position.next();
    }

    return avgClientSpot;
  }
  createAvgMktSpotColumn(title: string): BfxColDef {
    let avgMarketSpot: BfxColDef = this.initColdDef();
    avgMarketSpot.headerName = title;
    avgMarketSpot.field = OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_MKT_VWAP_SPOT;
    avgMarketSpot.indexType = IndexType.NaturalString;
    avgMarketSpot.cellClass = [];
    avgMarketSpot.minWidth = this.oneWs;
    avgMarketSpot.cellClass = ["blotter_center_align_numeric"];
    avgMarketSpot.width = this.fourWs;
    avgMarketSpot.columnFilterType = ColumnFilterType.NONE;
    avgMarketSpot.valueGetter = function (this: OrdersTableDefinitionFactory, params) {
      return this.myDealtPricingService.getVwap(params.data, params.colDef.field);
    }.bind(this);

    if (!this.restoreFromConfig(avgMarketSpot)) {
      avgMarketSpot.hide = true;
      avgMarketSpot.position = this.position.next();
    }
    return avgMarketSpot;
  }
}
